/**
 * Horizon Cart Bundle
 * Auto-generated from Shopify Horizon theme files.
 * Do NOT edit directly — regenerate via /tmp/bundle_horizon_cart.py
 */
(function() {
  'use strict';

  // Provide Theme global fallback if shim did not run (defensive).
  window.Theme = window.Theme || {};
  window.Theme.routes = window.Theme.routes || {};
  window.Theme.translations = window.Theme.translations || {};

  // Provide a no-op cartPerformance since performance.js is excluded from the bundle.
  const cartPerformance = {
    createStartingMarker: function() {},
    measureFromMarker: function() {},
    measureFromEvent: function() {},
  };

  // ===== utilities.js =====
  /**
   * Request an idle callback or fallback to setTimeout
   * @returns {function} The requestIdleCallback function
   */
  const requestIdleCallback =
    typeof window.requestIdleCallback == 'function' ? window.requestIdleCallback : setTimeout;

  /**
   * Returns a promise that resolves after yielding to the main thread.
   * @see https://web.dev/articles/optimize-long-tasks#scheduler-yield
   */
  const yieldToMainThread = () => {
    if ('yield' in scheduler) {
      // @ts-ignore - TypeScript doesn't recognize the yield method yet.
      return scheduler.yield();
    }

    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        setTimeout(resolve, 0);
      });
    });
  };

  /**
   * Tells if we are on a low power device based on the number of CPU cores and RAM
   * @returns {boolean} True if the device is a low power device, false otherwise
   */
  function isLowPowerDevice() {
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }

  /**
   * Check if the browser supports View Transitions API
   * @returns {boolean} True if the browser supports View Transitions API, false otherwise
   */
  function supportsViewTransitions() {
    return typeof document.startViewTransition === 'function';
  }

  /**
   * The current view transition
   * @type {{ current: Promise<void> | undefined }}
   */
  const viewTransition = {
    current: undefined,
  };

  /**
   * Functions to run when a view transition of a given type is started
   * @type {{ [key: string]: () => Promise<(() => void) | undefined> }}
   */
  const viewTransitionTypes = {
    'product-grid': async () => {
      const grid = document.querySelector('.product-grid');
      const productCards = /** @type {HTMLElement[]} */ ([
        ...document.querySelectorAll('.product-grid .product-grid__item'),
      ]);

      if (!grid || !productCards.length) return;

      await new Promise((resolve) =>
        requestIdleCallback(() => {
          const cardsToAnimate = getCardsToAnimate(grid, productCards);

          productCards.forEach((card, index) => {
            if (index < cardsToAnimate) {
              card.style.setProperty('view-transition-name', `product-card-${card.dataset.productId}`);
            } else {
              card.style.setProperty('content-visibility', 'hidden');
            }
          });

          resolve(null);
        })
      );

      return () =>
        productCards.forEach((card) => {
          card.style.removeProperty('view-transition-name');
          card.style.removeProperty('content-visibility');
        });
    },
  };

  /**
   * Starts a view transition
   * @param {() => void} callback The callback to call when the view transition starts
   * @param {string[]} [types] The types of view transition to use
   * @returns {Promise<void>} A promise that resolves when the view transition finishes
   */
  function startViewTransition(callback, types) {
    // Check if the API is supported and transitions are desired
    if (!supportsViewTransitions() || isLowPowerDevice() || prefersReducedMotion()) {
      callback();
      return Promise.resolve();
    }

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      let cleanupFunctions = [];

      if (types) {
        for (const type of types) {
          if (viewTransitionTypes[type]) {
            const cleanupFunction = await viewTransitionTypes[type]();
            if (cleanupFunction) cleanupFunctions.push(cleanupFunction);
          }
        }
      }

      const transition = document.startViewTransition(callback);

      if (!viewTransition.current) {
        viewTransition.current = transition.finished;
      }

      if (types) types.forEach((type) => transition.types.add(type));

      transition.finished.then(() => {
        viewTransition.current = undefined;
        cleanupFunctions.forEach((cleanupFunction) => cleanupFunction());
        resolve();
      });
    });
  }

  /**
   * @typedef {{ [key: string]: string | undefined }} Headers
   */

  /**
   * @typedef {Object} FetchConfig
   * @property {string} method
   * @property {Headers} headers
   * @property {string | FormData | undefined} [body]
   */

  /**
   * Creates a fetch configuration object
   * @param {string} [type] The type of response to expect
   * @param {Object} [config] The config of the request
   * @param {FetchConfig['body']} [config.body] The body of the request
   * @param {FetchConfig['headers']} [config.headers] The headers of the request
   * @returns {RequestInit} The fetch configuration object
   */
  function fetchConfig(type = 'json', config = {}) {
    /** @type {Headers} */
    const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };

    if (type === 'javascript') {
      headers['X-Requested-With'] = 'XMLHttpRequest';
      delete headers['Content-Type'];
    }

    return {
      method: 'POST',
      headers: /** @type {HeadersInit} */ (headers),
      body: config.body,
    };
  }

  /**
   * Creates a debounced function that delays calling the provided function (fn)
   * until after wait milliseconds have elapsed since the last time
   * the debounced function was invoked. The returned function has a .cancel()
   * method to cancel any pending calls.
   *
   * @template {(...args: any[]) => any} T
   * @param {T} fn The function to debounce
   * @param {number} wait The time (in milliseconds) to wait before calling fn
   * @returns {T & { cancel(): void }} A debounced version of fn with a .cancel() method
   */
  function debounce(fn, wait) {
    /** @type {number | undefined} */
    let timeout;

    /** @param {...any} args */
    function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    }

    // Add the .cancel method:
    debounced.cancel = () => {
      clearTimeout(timeout);
    };

    return /** @type {T & { cancel(): void }} */ (debounced);
  }

  /**
   * Creates a throttled function that calls the provided function (fn) at most once per every wait milliseconds
   *
   * @template {(...args: any[]) => any} T
   * @param {T} fn The function to throttle
   * @param {number} delay The time (in milliseconds) to wait before calling fn
   * @returns {T & { cancel(): void }} A throttled version of fn with a .cancel() method
   */
  function throttle(fn, delay) {
    let lastCall = 0;

    /** @param {...any} args */
    function throttled(...args) {
      const now = performance.now();
      // If the time since the last call exceeds the delay, execute the callback
      if (now - lastCall >= delay) {
        lastCall = now;
        fn.apply(this, args);
      }
    }

    throttled.cancel = () => {
      lastCall = performance.now();
    };

    return /** @type {T & { cancel(): void }} */ (throttled);
  }

  /**
   * A media query for reduced motion
   * @type {MediaQueryList}
   */
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Check if the user prefers reduced motion
   * @returns {boolean} True if the user prefers reduced motion, false otherwise
   */
  function prefersReducedMotion() {
    return reducedMotion.matches;
  }

  /**
   * Normalize a string
   * @param {string} str The string to normalize
   * @returns {string} The normalized string
   */
  function normalizeString(str) {
    return str
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();
  }

  /**
   * Check if the document is ready/loaded and call the callback when it is.
   * @param {() => void} callback The function to call when the document is ready.
   */
  function onDocumentLoaded(callback) {
    if (document.readyState === 'complete') {
      callback();
    } else {
      window.addEventListener('load', callback);
    }
  }

  /**
   * Check if the DOM is ready and call the callback when it is.
   * This fires when the DOM is fully parsed but before all resources are loaded.
   * @param {() => void} callback The function to call when the DOM is ready.
   */
  function onDocumentReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  /**
   * Removes will-change from an element after an animation ends.
   * Intended to be used as an animationend event listener.
   * @param {AnimationEvent} event The animation event.
   */
  function removeWillChangeOnAnimationEnd(event) {
    const target = event.target;
    if (target && target instanceof HTMLElement) {
      target.style.setProperty('will-change', 'unset');
      target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
    }
  }

  /**
   * Wait for all animations to finish before calling the callback.
   * @param {Element | Element[]} elements The element(s) whose animations to wait for.
   * @param {() => void} [callback] The function to call when all animations are finished.
   * @param {Object} [options] The options to pass to `Element.getAnimations`.
   * @returns {Promise<void>} A promise that resolves when all animations are finished.
   */
  function onAnimationEnd(elements, callback, options = { subtree: true }) {
    const animations = Array.isArray(elements)
      ? elements.flatMap((element) => element.getAnimations(options))
      : elements.getAnimations(options);
    const animationPromises = animations.reduce((acc, animation) => {
      // Ignore ViewTimeline animations
      if (animation.timeline instanceof DocumentTimeline) {
        acc.push(animation.finished);
      }

      return acc;
    }, /** @type {Promise<Animation>[]} */ ([]));

    return Promise.allSettled(animationPromises).then(callback);
  }

  /**
   * Check if the click is outside the element.
   * @param {MouseEvent} event The mouse event.
   * @param {Element} element The element to check.
   * @returns {boolean} True if the click is outside the element, false otherwise.
   */
  function isClickedOutside(event, element) {
    if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
      return !isPointWithinElement(event.clientX, event.clientY, element);
    }

    return !element.contains(event.target);
  }

  /**
   * Check if a point is within an element.
   * @param {number} x The x coordinate of the point.
   * @param {number} y The y coordinate of the point.
   * @param {Element} element The element to check.
   * @returns {boolean} True if the point is within the element, false otherwise.
   */
  function isPointWithinElement(x, y, element) {
    const { left, right, top, bottom } = element.getBoundingClientRect();

    return x >= left && x <= right && y >= top && y <= bottom;
  }

  /**
   * A media query for large screens
   * @type {MediaQueryList}
   */
  const mediaQueryLarge = matchMedia('(min-width: 750px)');

  /**
   * Check if the current breakpoint is mobile
   * @returns {boolean} True if the current breakpoint is mobile, false otherwise
   */
  function isMobileBreakpoint() {
    return !mediaQueryLarge.matches;
  }

  /**
   * Check if the current breakpoint is desktop
   * @returns {boolean} True if the current breakpoint is desktop, false otherwise
   */
  function isDesktopBreakpoint() {
    return mediaQueryLarge.matches;
  }

  /**
   * Check if the device is a touch device independently ot the screen size
   * @returns {boolean} True if the device is a touch device, false otherwise
   */
  function isTouchDevice() {
    return 'ontouchstart' in window && navigator.maxTouchPoints > 0;
  }

  /**
   * Clamps a number between a minimum and maximum value.
   * @param {number} value - The input number to clamp.
   * @param {number} min - The minimum value.
   * @param {number} max - The maximum value.
   * @returns {number} The clamped value.
   */
  function clamp(value, min, max) {
    return Math.max(min, Math.min(value, max));
  }

  /**
   * Calculates the center point of an element along the specified axis.
   * @param {Element} element - The DOM element to find the center of.
   * @param {'x' | 'y'} [axis] - The axis ('x' or 'y') to get the center for. If not provided, returns both axes.
   * @template {('x' | 'y')} T
   * @param {T} [axis]
   * @returns {T extends ('x' | 'y') ? number : {x: number, y: number}} The center point along the axis or an object with x and y coordinates.
   */
  function center(element, axis) {
    const { left, width, top, height } = element.getBoundingClientRect();
    const point = {
      x: left + width / 2,
      y: top + height / 2,
    };

    if (axis) return /**  @type {any} */ (point[axis]);

    return /**  @type {any} */ (point);
  }

  /**
   * Calculates the start point of an element along the specified axis.
   * @param {Element} element - The DOM element to find the start of.
   * @param {'x' | 'y'} [axis] - The axis ('x' or 'y') to get the start for. If not provided, returns both axes.
   * @returns {number | {x: number, y: number}} The start point along the axis or an object with x and y coordinates.
   */
  function start(element, axis) {
    const { left, top } = element.getBoundingClientRect();
    const point = { x: left, y: top };

    if (axis) return /**  @type {any} */ (point[axis]);

    return /**  @type {any} */ (point);
  }

  /**
   * Finds the value in an array that is closest to a target value.
   * @param {number[]} values - An array of numbers.
   * @param {number} target - The target number to find the closest value to.
   * @returns {number} The value from the array closest to the target.
   */
  function closest(values, target) {
    return values.reduce(function (prev, curr) {
      return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
    });
  }

  /**
   * Prevents the default action of an event.
   * @param {Event} event - The event to prevent the default action of.
   */
  function preventDefault(event) {
    event.preventDefault();
  }

  /**
   * Get the visible elements within a root element.
   * @template {Element} T
   * @param {Element} root - The element within which elements should be visible.
   * @param {T[] | undefined} elements - The elements to check for visibility.
   * @param {number} [ratio=1] - The minimum percentage of the element that must be visible.
   * @param {'x' | 'y'} [axis] - Whether to only check along 'x' axis, 'y' axis, or both if undefined.
   * @returns {T[]} An array containing the visible elements.
   */
  function getVisibleElements(root, elements, ratio = 1, axis) {
    if (!elements?.length) return [];
    const rootRect = root.getBoundingClientRect();

    return elements.filter((element) => {
      const { width, height, top, right, left, bottom } = element.getBoundingClientRect();

      if (ratio < 1) {
        const intersectionLeft = Math.max(rootRect.left, left);
        const intersectionRight = Math.min(rootRect.right, right);
        const intersectionWidth = Math.max(0, intersectionRight - intersectionLeft);

        if (axis === 'x') {
          return width > 0 && intersectionWidth / width >= ratio;
        }

        const intersectionTop = Math.max(rootRect.top, top);
        const intersectionBottom = Math.min(rootRect.bottom, bottom);
        const intersectionHeight = Math.max(0, intersectionBottom - intersectionTop);

        if (axis === 'y') {
          return height > 0 && intersectionHeight / height >= ratio;
        }

        const intersectionArea = intersectionWidth * intersectionHeight;
        const elementArea = width * height;

        // Check that at least the specified ratio of the element is visible
        return elementArea > 0 && intersectionArea / elementArea >= ratio;
      }

      const isWithinX = left >= rootRect.left && right <= rootRect.right;
      if (axis === 'x') {
        return isWithinX;
      }

      const isWithinY = top >= rootRect.top && bottom <= rootRect.bottom;
      if (axis === 'y') {
        return isWithinY;
      }

      return isWithinX && isWithinY;
    });
  }

  function getIOSVersion() {
    const { userAgent } = navigator;
    const isIOS = /(iPhone|iPad)/i.test(userAgent);

    if (!isIOS) return null;

    const version = userAgent.match(/OS ([\d_]+)/)?.[1];
    const [major, minor] = version?.split('_') || [];
    if (!version || !major) return null;

    return {
      fullString: version.replace('_', '.'),
      major: parseInt(major, 10),
      minor: minor ? parseInt(minor, 10) : 0,
    };
  }

  /**
   * Determines which grid items should be animated during a transition.
   * It makes an estimation based on the zoom-out card size because it's
   * the common denominator for both transition states. I.e. transitioning either
   * from 10 to 20 cards the other way around, both need 20 cards to be animated.
   * @param {Element} grid - The grid element
   * @param {Element[]} cards - The cards to animate
   * @returns {number} - Number of cards that should be animated
   */
  function getCardsToAnimate(grid, cards) {
    if (!grid || !cards || cards.length === 0) return 0;

    const itemSample = cards[0];
    if (!itemSample) return 0;

    // Calculate the visible area of the grid for the Y axis. Assume X is always fully visible:
    const gridRect = grid.getBoundingClientRect();
    const visibleArea = {
      top: Math.max(0, gridRect.top),
      bottom: Math.min(window.innerHeight, gridRect.bottom),
    };

    const visibleHeight = Math.round(visibleArea.bottom - visibleArea.top);
    if (visibleHeight <= 0) return 0;

    /** @type {import('product-card').ProductCard | null} */
    const cardSample = itemSample.querySelector('product-card');
    const gridStyle = getComputedStyle(grid);

    const galleryAspectRatio = cardSample?.refs?.cardGallery?.style.getPropertyValue('--gallery-aspect-ratio') || '';
    let aspectRatio = parseFloat(galleryAspectRatio) || 0.5;
    if (galleryAspectRatio?.includes('/')) {
      const [width = '1', height = '2'] = galleryAspectRatio.split('/');
      aspectRatio = parseInt(width, 10) / parseInt(height, 10);
    }

    const cardGap = parseInt(cardSample?.refs?.productCardLink?.style.getPropertyValue('--product-card-gap') || '') || 12;
    const gridGap = parseInt(gridStyle.getPropertyValue('--product-grid-gap')) || 12;

    // Assume only a couple of lines of text in the card details (title and price).
    // If the title wraps into more lines, we might just animate more cards, but that's fine.
    const detailsSize = ((parseInt(gridStyle.fontSize) || 16) + 2) * 2;

    const isMobile = window.innerWidth < 750;

    // Always use the zoom-out state card width
    const cardWidth = isMobile ? Math.round((gridRect.width - gridGap) / 2) : 100;
    const cardHeight = Math.round(cardWidth / aspectRatio) + cardGap + detailsSize;

    // Calculate the number of cards that fit in the visible area:
    // - The width estimation is pretty accurate, we can ignore decimals.
    // - The height estimation needs to account for peeking rows, so we round up.
    const columnsInGrid = isMobile ? 2 : Math.floor((gridRect.width + gridGap) / (cardWidth + gridGap));
    const rowsInGrid = Math.ceil((visibleHeight - gridGap) / (cardHeight + gridGap));

    return columnsInGrid * rowsInGrid;
  }

  /**
   * Preloads an image
   * @param {string} src - The source of the image to preload
   */
  function preloadImage(src) {
    const image = new Image();
    image.src = src;
  }

  class TextComponent extends HTMLElement {
    shimmer() {
      this.setAttribute('shimmer', '');
    }
  }

  if (!customElements.get('text-component')) {
    customElements.define('text-component', TextComponent);
  }

  /**
   * Resets the shimmer attribute on all elements in the container.
   * @param {Element} [container] - The container to reset the shimmer attribute on.
   */
  function resetShimmer(container = document.body) {
    const shimmer = container.querySelectorAll('[shimmer]');
    shimmer.forEach((item) => item.removeAttribute('shimmer'));
  }

  /**
   * Change the meta theme color of the browser.
   * @param {string} color - The color value (e.g., 'rgb(255, 255, 255)')
   */
  function changeMetaThemeColor(color) {
    const metaThemeColor = document.head.querySelector('meta[name="theme-color"]');
    if (metaThemeColor && color) {
      metaThemeColor.setAttribute('content', color);
    }
  }

  /**
   * Gets the `view` URL search parameter value, if it exists.
   * Useful for Section Rendering API calls to get HTML markup for the correct template view.
   * Primarily used in testing alternative template views.
   * @returns {string | null} The view parameter value, or null if it doesn't exist
   */
  function getViewParameterValue() {
    return new URLSearchParams(window.location.search).get('view');
  }

  /**
   * Helper to parse integer with a default fallback
   * Handles the case where 0 is a valid value (not falsy)
   * @template {number|null} T
   * @param {string|number|null|undefined} value - The value to parse
   * @param {T} defaultValue - The default value (number or null)
   * @returns {number|T} The parsed integer or default value
   */
  function parseIntOrDefault(value, defaultValue) {
    if (value === null || value === undefined || value === '') {
      return defaultValue;
    }
    const parsed = parseInt(value.toString());
    return isNaN(parsed) ? defaultValue : parsed;
  }

  class Scheduler {
    /** @type {Set<() => void>} */
    #queue = new Set();
    /** @type {boolean} */
    #scheduled = false;

    /** @param {() => void} task */
    schedule = async (task) => {
      this.#queue.add(task);

      if (!this.#scheduled) {
        this.#scheduled = true;

        // Wait for any in-progress view transitions to finish
        if (viewTransition.current) await viewTransition.current;

        requestAnimationFrame(this.flush);
      }
    };

    flush = () => {
      for (const task of this.#queue) {
        setTimeout(task, 0);
      }

      this.#queue.clear();
      this.#scheduled = false;
    };
  }

  const scheduler = new Scheduler();

  /**
   * Executes a callback once per session when in the Shopify theme editor
   * @param {HTMLElement} element - The element to check for the shopify editor block id
   * @param {string} sessionKeyName - Unique key for the session storage
   * @param {() => void} callback - Function to execute
   * @returns {void} - Void if the callback was executed, undefined if it wasn't
   */
  function oncePerEditorSession(element, sessionKeyName, callback) {
    const isInThemeEditor = window.Shopify?.designMode;
    const shopifyEditorSectionId = JSON.parse(element.dataset.shopifyEditorSection || '{}').id;
    const shopifyEditorBlockId = JSON.parse(element.dataset.shopifyEditorBlock || '{}').id;
    const editorId = shopifyEditorSectionId || shopifyEditorBlockId;
    const uniqueSessionKey = `${sessionKeyName}-${editorId}`;

    if (isInThemeEditor && sessionStorage.getItem(uniqueSessionKey)) return;

    callback();

    if (isInThemeEditor) sessionStorage.setItem(uniqueSessionKey, 'true');

    return;
  }

  /**
   * A custom ResizeObserver that only calls the callback when the element is resized.
   * By default the ResizeObserver callback is called when the element is first observed.
   */
  class ResizeNotifier extends ResizeObserver {
    #initialized = false;

    /**
     * @param {ResizeObserverCallback} callback
     */
    constructor(callback) {
      super((entries) => {
        if (this.#initialized) return callback(entries, this);
        this.#initialized = true;
      });
    }

    disconnect() {
      this.#initialized = false;
      super.disconnect();
    }
  }

  /**
   * Sets the menuStyle dataset attribute on the header component element.
   */
  function setHeaderMenuStyle() {
    const headerComponent = /** @type {HTMLElement} | null */ (document.querySelector('#header-component'));
    if (headerComponent) {
      window.requestAnimationFrame(() => {
        const overflowList = headerComponent?.querySelector('overflow-list');
        const hasReachedMinimum = overflowList && overflowList.hasAttribute('minimum-reached');
        headerComponent.dataset.menuStyle = isTouchDevice() || hasReachedMinimum ? 'drawer' : 'menu';
      });
    }
  }

  /**
   * Header group includes the header (with the menu, etc) and other sections like announcements, dividers, etc.
   * @param {HTMLElement | null} header - The header element
   * @param {HTMLElement | null} headerGroup - The header group element, defaults to the #header-group element
   * @returns {number} The height of the header group
   */
  function calculateHeaderGroupHeight(
    header = document.querySelector('#header-component'),
    headerGroup = document.querySelector('#header-group')
  ) {
    if (!headerGroup) return 0;

    let totalHeight = 0;
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      totalHeight += element.offsetHeight;
    }

    // If the header is transparent and has a sibling section, add the height of the header to the total height
    if (header instanceof HTMLElement && header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
      return totalHeight + header.offsetHeight;
    }

    return totalHeight;
  }

  /**
   * Updates CSS custom properties for transparent header offset calculation
   * Avoids expensive :has() selectors
   */
  function updateTransparentHeaderOffset() {
    const header = document.querySelector('#header-component');
    const headerGroup = document.querySelector('#header-group');
    const hasHeaderSection = headerGroup?.querySelector('.header-section');
    if (!hasHeaderSection || !header?.hasAttribute('transparent')) {
      document.body.style.setProperty('--transparent-header-offset-boolean', '0');
      return;
    }

    const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains('shopify-section');

    const shouldApplyOffset = !hasImmediateSection ? '1' : '0';
    document.body.style.setProperty('--transparent-header-offset-boolean', shouldApplyOffset);
  }

  /**
   * Initialize and maintain header height CSS variables.
   */
  function updateHeaderHeights() {
    const header = document.querySelector('header-component');

    // Early exit if no header - nothing to do
    if (!(header instanceof HTMLElement)) return;

    // Calculate initial heights
    const headerHeight = header.offsetHeight;
    const headerGroupHeight = calculateHeaderGroupHeight(header);
    const headerTopRow = /** @type {HTMLElement} | null */ (header.querySelector('.header__row--top'));

    document.body.style.setProperty('--header-height', `${headerHeight}px`);
    document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);

    if (headerTopRow) {
      window.requestAnimationFrame(function () {
        header.style.setProperty('--top-row-height', `${headerTopRow.offsetHeight}px`);
      });
    }
  }

  function updateAllHeaderCustomProperties() {
    updateHeaderHeights();
    updateTransparentHeaderOffset();
    setHeaderMenuStyle();
  }

  // Theme is not defined in some layouts, like the gift card page
  if (typeof Theme !== 'undefined') {
    Theme.utilities = {
      ...Theme.utilities,
      scheduler: scheduler,
    };
  }


  // ===== events.js =====
  /**
   * @namespace ThemeEvents
   * @description A collection of theme-specific events that can be used to trigger and listen for changes anywhere in the theme.
   * @example
   * document.dispatchEvent(new VariantUpdateEvent(variant, sectionId, { html }));
   * document.addEventListener(ThemeEvents.variantUpdate, (e) => { console.log(e.detail.variant) });
   */
  class ThemeEvents {
    /** @static @constant {string} Event triggered when a variant is selected */
    static variantSelected = 'variant:selected';
    /** @static @constant {string} Event triggered when a variant is changed */
    static variantUpdate = 'variant:update';
    /** @static @constant {string} Event triggered when the cart items or quantities are updated */
    static cartUpdate = 'cart:update';
    /** @static @constant {string} Event triggered when a cart update fails */
    static cartError = 'cart:error';
    /** @static @constant {string} Event triggered when a media (video, 3d model) is loaded */
    static mediaStartedPlaying = 'media:started-playing';
    // Event triggered when quantity-selector value is changed
    static quantitySelectorUpdate = 'quantity-selector:update';
    /** @static @constant {string} Event triggered when a predictive search is expanded */
    static megaMenuHover = 'megaMenu:hover';
    /** @static @constant {string} Event triggered when a zoom dialog media is selected */
    static zoomMediaSelected = 'zoom-media:selected';
    /** @static @constant {string} Event triggered when a discount is applied */
    static discountUpdate = 'discount:update';
    /** @static @constant {string} Event triggered when changing collection filters */
    static FilterUpdate = 'filter:update';
  }

  /**
   * Event fired when a variant is selected
   * @extends {Event}
   */
  class VariantSelectedEvent extends Event {
    /**
     * Creates a new VariantSelectedEvent
     * @param {Object} resource - The new variant object
     * @param {string} resource.id - The option value id
     */
    constructor(resource) {
      super(ThemeEvents.variantSelected, { bubbles: true });
      this.detail = {
        resource,
      };
    }
  }

  /**
   * Event fired after a variant is updated
   * @extends {Event}
   */
  class VariantUpdateEvent extends Event {
    /**
     * Creates a new VariantUpdateEvent
     * @param {Object} resource - The new variant object
     * @param {string} resource.id - The id of the variant
     * @param {boolean} resource.available - Whether the variant is available
     * @param {boolean} resource.inventory_management - Whether the variant has inventory management
     * @param {string} [resource.sku] - The SKU of the variant
     * @param {Object} [resource.featured_media] - The featured media of the variant
     * @param {string} [resource.featured_media.id] - The id of the featured media
     * @param {Object} [resource.featured_media.preview_image] - The preview image of the featured media
     * @param {string} [resource.featured_media.preview_image.src] - The src URL of the preview image
     * @param {string} sourceId - The id of the element the action was triggered from
     * @param {Object} data - Additional event data
     * @param {Document} data.html - The new document fragment for the variant
     * @param {string} data.productId - The product ID of the updated variant, used to ensure the correct product form is updated
     * @param {Object} [data.newProduct] - If a new product was loaded as part of the variant update (combined listing)
     * @param {string} data.newProduct.id - The id of the new product
     * @param {string} data.newProduct.url - The url of the new product
     */
    constructor(resource, sourceId, data) {
      super(ThemeEvents.variantUpdate, { bubbles: true });
      this.detail = {
        resource: resource || null,
        sourceId,
        data: {
          html: data.html,
          productId: data.productId,
          newProduct: data.newProduct,
        },
      };
    }
  }

  /**
   * Event class for cart additions
   * @extends {Event}
   */
  class CartAddEvent extends Event {
    /**
     * Creates a new CartAddEvent
     * @param {Object} [resource] - The new cart object
     * @param {string} [sourceId] - The id of the element the action was triggered from
     * @param {Object} [data] - Additional event data
     * @param {boolean} [data.didError] - Whether the cart operation failed
     * @param {string} [data.source] - The source of the cart update
     * @param {string} [data.productId] - The id of the product card that was updated
     * @param {number} [data.itemCount] - The number of items in the cart
     * @param {string} [data.variantId] - The id of the product variant that was added
     * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
     */
    constructor(resource, sourceId, data) {
      super(CartAddEvent.eventName, { bubbles: true });
      this.detail = {
        resource,
        sourceId,
        data: {
          ...data,
        },
      };
    }

    static eventName = ThemeEvents.cartUpdate;
  }

  /**
   * Event class for cart updates
   * @extends {Event}
   */
  class CartUpdateEvent extends Event {
    /**
     * Creates a new CartUpdateEvent
     * @param {Object} resource - The new cart object
     * @param {string} sourceId - The id of the element the action was triggered from
     * @param {Object} [data] - Additional event data
     * @param {boolean} [data.didError] - Whether the cart operation failed
     * @param {string} [data.source] - The source of the cart update
     * @param {string} [data.productId] - The id of the product card that was updated
     * @param {number} [data.itemCount] - The number of items in the cart
     * @param {string} [data.variantId] - The id of the product variant that was updated
     * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
     */
    constructor(resource, sourceId, data) {
      super(ThemeEvents.cartUpdate, { bubbles: true });
      this.detail = {
        resource,
        sourceId,
        data: {
          ...data,
        },
      };
    }
  }

  /**
   * Event class for cart errors
   * @extends {Event}
   */
  class CartErrorEvent extends Event {
    /**
     * Creates a new CartErrorEvent
     * @param {string} sourceId - The id of the element the action was triggered from
     * @param {string} message - A message from the server response
     * @param {Object} description - Description from the server response
     * @param {Object} errors - Errors from the server response
     */
    constructor(sourceId, message, description, errors) {
      super(ThemeEvents.cartError, { bubbles: true });
      this.detail = {
        sourceId,
        data: {
          message,
          errors,
          description,
        },
      };
    }
  }

  /**
   * Event class for quantity-selector updates
   * @extends {Event}
   */
  class QuantitySelectorUpdateEvent extends Event {
    /**
     * Creates a new QuantitySelectorUpdateEvent
     * @param {number} quantity - Quantity value
     * @param {number} [cartLine] - The id of the updated cart line
     */
    constructor(quantity, cartLine) {
      super(ThemeEvents.quantitySelectorUpdate, { bubbles: true });
      this.detail = {
        quantity,
        cartLine,
      };
    }
  }

  /**
   * Event class for quantity-selector updates
   * @extends {Event}
   */
  class DiscountUpdateEvent extends Event {
    /**
     * Creates a new DiscountUpdateEvent
     * @param {Object} resource - The new cart object
     * @param {string} sourceId - The id of the element the action was triggered from
     */
    constructor(resource, sourceId) {
      super(ThemeEvents.discountUpdate, { bubbles: true });
      this.detail = {
        resource,
        sourceId,
      };
    }
  }

  /**
   * Event class for media playback starts
   * @extends {Event}
   */
  class MediaStartedPlayingEvent extends Event {
    /**
     * Creates a new MediaStartedPlayingEvent
     * @param {HTMLElement} resource - The element containing the video that emitted the event
     */
    constructor(resource) {
      super(ThemeEvents.mediaStartedPlaying, { bubbles: true });
      this.detail = {
        resource,
      };
    }
  }

  /**
   * @typedef {Object} SlideshowSelectEventData
   * @property {number} index
   * @property {string | null} id
   * @property {Element} slide
   * @property {number} previousIndex
   * @property {boolean} userInitiated
   * @property {'select' | 'scroll' | 'drag'} trigger
   */

  class SlideshowSelectEvent extends Event {
    /**  @param {SlideshowSelectEventData} data */
    constructor(data) {
      super(SlideshowSelectEvent.eventName, { bubbles: true });
      this.detail = data;
    }

    /** @type {SlideshowSelectEventData} */
    detail;

    static eventName = 'slideshow:select';
  }

  /**
   * Event class for zoom dialog media selection
   * @extends {Event}
   */
  class ZoomMediaSelectedEvent extends Event {
    /**
     * Creates a new ZoomMediaSelectedEvent
     * @param {number} index - The index of the selected media
     */
    constructor(index) {
      super(ThemeEvents.zoomMediaSelected, { bubbles: true });
      this.detail = {
        index,
      };
    }
  }

  /**
   * Event class for mega menu hover being hovered over
   * @extends {Event}
   */
  class MegaMenuHoverEvent extends Event {
    constructor() {
      super(ThemeEvents.megaMenuHover, { bubbles: true });
    }
  }

  /** Event class for facet filtering updates */
  class FilterUpdateEvent extends Event {
    /** @param {URLSearchParams} queryParams */
    constructor(queryParams) {
      super(ThemeEvents.FilterUpdate, { bubbles: true });
      this.detail = {
        queryParams,
      };
    }

    shouldShowClearAll() {
      return [...this.detail.queryParams.entries()].filter(([key]) => key.startsWith('filter.')).length > 0;
    }
  }


  // ===== component.js =====

  /*
   * Declarative shadow DOM is only initialized on the initial render of the page.
   * If the component is mounted after the browser finishes the initial render,
   * the shadow root needs to be manually hydrated.
   */
  class DeclarativeShadowElement extends HTMLElement {
    connectedCallback() {
      if (!this.shadowRoot) {
        const template = this.querySelector(':scope > template[shadowrootmode="open"]');

        if (!(template instanceof HTMLTemplateElement)) return;

        const shadow = this.attachShadow({ mode: 'open' });
        shadow.append(template.content.cloneNode(true));
      }
    }
  }

  /**
   * @typedef {Record<string, Element | Element[] | undefined>} Refs
   */

  /**
   * @template {Refs} T
   * @typedef {T & Refs} RefsType
   */

  /**
   * Base class that powers our custom web components.
   *
   * Manages references to child elements with `ref` attributes and sets up mutation observers to keep
   * the refs updated when the DOM changes. Also handles declarative event listeners using.
   *
   * @template {Refs} [T=Refs]
   * @extends {DeclarativeShadowElement}
   */
  class Component extends DeclarativeShadowElement {
    /**
     * An object holding references to child elements with `ref` attributes.
     *
     * @type {RefsType<T>}
     */
    refs = /** @type {RefsType<T>} */ ({});

    /**
     * An array of required refs. If a ref is not found, an error will be thrown.
     *
     * @type {string[] | undefined}
     */
    requiredRefs;

    /**
     * Gets the root node of the component, which is either its shadow root or the component itself.
     *
     * @returns {(ShadowRoot | Component<T>)[]} The root nodes.
     */
    get roots() {
      return this.shadowRoot ? [this, this.shadowRoot] : [this];
    }

    /**
     * Called when the element is connected to the document's DOM.
     *
     * Initializes event listeners and refs.
     */
    connectedCallback() {
      super.connectedCallback();
      registerEventListeners();

      this.#updateRefs();

      requestIdleCallback(() => {
        for (const root of this.roots) {
          this.#mutationObserver.observe(root, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['ref'],
            attributeOldValue: true,
          });
        }
      });
    }

    /**
     * Called when the element is re-rendered by the Section Rendering API.
     */
    updatedCallback() {
      this.#mutationObserver.takeRecords();
      this.#updateRefs();
    }

    /**
     * Called when the element is disconnected from the document's DOM.
     *
     * Disconnects the mutation observer.
     */
    disconnectedCallback() {
      this.#mutationObserver.disconnect();
    }

    /**
     * Updates the `refs` object by querying all descendant elements with `ref` attributes and storing references to them.
     *
     * This method is called to keep the `refs` object in sync with the DOM.
     */
    #updateRefs() {
      const refs = /** @type any */ ({});
      const elements = this.roots.reduce((acc, root) => {
        for (const element of root.querySelectorAll('[ref]')) {
          if (!this.#isDescendant(element)) continue;
          acc.add(element);
        }

        return acc;
      }, /** @type {Set<Element>} */ (new Set()));

      for (const ref of elements) {
        const refName = ref.getAttribute('ref') ?? '';
        const isArray = refName.endsWith('[]');
        const path = isArray ? refName.slice(0, -2) : refName;

        if (isArray) {
          const array = Array.isArray(refs[path]) ? refs[path] : [];

          array.push(ref);
          refs[path] = array;
        } else {
          refs[path] = ref;
        }
      }

      if (this.requiredRefs?.length) {
        for (const ref of this.requiredRefs) {
          if (!(ref in refs)) {
            throw new MissingRefError(ref, this);
          }
        }
      }

      this.refs = /** @type {RefsType<T>} */ (refs);
    }

    /**
     * MutationObserver instance to observe changes in the component's DOM subtree and update refs accordingly.
     *
     * @type {MutationObserver}
     */
    #mutationObserver = new MutationObserver((mutations) => {
      if (
        mutations.some(
          (m) =>
            (m.type === 'attributes' && this.#isDescendant(m.target)) ||
            (m.type === 'childList' && [...m.addedNodes, ...m.removedNodes].some(this.#isDescendant))
        )
      ) {
        this.#updateRefs();
      }
    });

    /**
     * Checks if a given node is a descendant of this component.
     *
     * @param {Node} node - The node to check.
     * @returns {boolean} True if the node is a descendant of this component.
     */
    #isDescendant = (node) => getClosestComponent(getAncestor(node)) === this;
  }

  /**
   * Get the ancestor of a given node.
   *
   * @param {Node} node - The node to get the ancestor of.
   * @returns {Node | null} The ancestor of the node or null if none is found.
   */
  function getAncestor(node) {
    if (node.parentNode) return node.parentNode;

    const root = node.getRootNode();
    if (root instanceof ShadowRoot) return root.host;

    return null;
  }

  /**
   * Recursively finds the closest ancestor that is an instance of `Component`.
   *
   * @param {Node | null} node - The starting node to search from.
   * @returns {HTMLElement | null} The closest ancestor `Component` instance or null if none is found.
   */
  function getClosestComponent(node) {
    if (!node) return null;
    if (node instanceof Component) return node;
    if (node instanceof HTMLElement && node.tagName.toLowerCase().endsWith('-component')) return node;

    const ancestor = getAncestor(node);
    if (ancestor) return getClosestComponent(ancestor);

    return null;
  }

  /**
   * Initializes the event listeners for custom event handling.
   *
   * Sets up event listeners for specified events and delegates the handling of those events
   * to methods defined on the closest `Component` instance, based on custom attributes.
   */
  let initialized = false;

  function registerEventListeners() {
    if (initialized) return;
    initialized = true;

    const events = ['click', 'change', 'select', 'focus', 'blur', 'submit', 'input', 'keydown', 'keyup', 'toggle'];
    const shouldBubble = ['focus', 'blur'];
    const expensiveEvents = ['pointerenter', 'pointerleave'];

    for (const eventName of [...events, ...expensiveEvents]) {
      const attribute = `on:${eventName}`;

      document.addEventListener(
        eventName,
        (event) => {
          const element = getElement(event);

          if (!element) return;

          const proxiedEvent =
            event.target !== element
              ? new Proxy(event, {
                  get(target, property) {
                    if (property === 'target') return element;

                    const value = Reflect.get(target, property);

                    if (typeof value === 'function') {
                      return value.bind(target);
                    }

                    return value;
                  },
                })
              : event;

          const value = element.getAttribute(attribute) ?? '';
          let [selector, method] = value.split('/');
          // Extract the last segment of the attribute value delimited by `?` or `/`
          // Do not use lookback for Safari 16.0 compatibility
          const matches = value.match(/([\/\?][^\/\?]+)([\/\?][^\/\?]+)$/);
          const data = matches ? matches[2] : null;
          const instance = selector
            ? selector.startsWith('#')
              ? document.querySelector(selector)
              : element.closest(selector)
            : getClosestComponent(element);

          if (!(instance instanceof Component) || !method) return;

          method = method.replace(/\?.*/, '');

          const callback = /** @type {any} */ (instance)[method];

          if (typeof callback === 'function') {
            try {
              /** @type {(Event | Data)[]} */
              const args = [proxiedEvent];

              if (data) args.unshift(parseData(data));

              callback.call(instance, ...args);
            } catch (error) {
              console.error(error);
            }
          }
        },
        { capture: true }
      );
    }

    /** @param {Event} event */
    function getElement(event) {
      const target = event.composedPath?.()[0] ?? event.target;

      if (!(target instanceof Element)) return;

      if (target.hasAttribute(`on:${event.type}`)) {
        return target;
      }

      if (expensiveEvents.includes(event.type)) {
        return null;
      }

      return event.bubbles || shouldBubble.includes(event.type) ? target.closest(`[on\\:${event.type}]`) : null;
    }
  }

  /**
   * Parses a string to extract data based on a delimiter.
   *
   * @param {string} str - The string to parse.
   * @returns {Object|Array<string|number>|string} The parsed data.
   */
  function parseData(str) {
    const delimiter = str[0];
    const data = str.slice(1);

    return delimiter === '?'
      ? Object.fromEntries(
          Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [key, parseValue(value)])
        )
      : parseValue(data);
  }

  /**
   * @typedef {Object|Array<string|number>|string} Data
   */

  /**
   * Parses a string value to its appropriate type.
   *
   * @param {string} str - The string to parse.
   * @returns {Data} The parsed value.
   */
  function parseValue(str) {
    if (str === 'true') return true;
    if (str === 'false') return false;

    const maybeNumber = Number(str);
    if (!isNaN(maybeNumber) && str.trim() !== '') return maybeNumber;

    return str;
  }

  /**
   * Throws a formatted error when a required ref is not found in the component.
   */
  class MissingRefError extends Error {
    /**
     * @param {string} ref
     * @param {Component} component
     */
    constructor(ref, component) {
      super(`Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`);
    }
  }


  // ===== morph.js =====

  /**
   * @typedef {Object} Options
   * @property {boolean} [childrenOnly] - Only update children
   * @property {(node: Node | undefined) => string|number|undefined} [getNodeKey] - Get node key for matching
   * @property {(oldNode: Node, newNode: Node) => void} [onBeforeUpdate] - Pre-update hook
   * @property {(node: Node) => void} [onAfterUpdate] - Post-update hook
   * @property {(oldNode: Node, newNode: Node) => boolean} [reject] - Reject a node from being morphed
   * @property {boolean} [hydrationMode] - If true, only morph subtrees whose elements have `data-hydration-key="<non-empty>"`, matched by that value
   */

  const HYDRATION_KEY_ATTRIBUTE = 'data-hydration-key';

  /**
   * The options for the morph
   * @type {Options}
   */
  const MORPH_OPTIONS = {
    childrenOnly: true,
    hydrationMode: false,
    reject(oldNode, newNode) {
      if (newNode.nodeType === Node.TEXT_NODE && newNode.nodeValue?.trim() === '') {
        return true;
      }

      if (
        newNode instanceof HTMLTemplateElement &&
        newNode.shadowRootMode === 'open' &&
        oldNode.parentElement &&
        newNode.parentElement &&
        oldNode.parentElement.tagName === newNode.parentElement.tagName &&
        oldNode.parentElement?.shadowRoot != null
      ) {
        // Ignore template elements of components that are already initialized
        return true;
      }

      if (newNode.nodeType === Node.COMMENT_NODE && newNode.nodeValue === 'shopify:rendered_by_section_api') {
        // Remove a comment node injected by the Section Rendering API in the Theme Editor
        return true;
      }

      return false;
    },
    onBeforeUpdate(oldNode, newNode) {
      if (oldNode instanceof Element && newNode instanceof Element) {
        const attributes = ['product-grid-view', 'data-current-checked', 'data-previous-checked', 'cart-summary-sticky'];

        for (const attribute of attributes) {
          const oldValue = oldNode.getAttribute(attribute);
          const newValue = newNode.getAttribute(attribute);

          if (oldValue && oldValue !== newValue) {
            newNode.setAttribute(attribute, oldValue);
          }
        }

        // Special case for elements that need to keep their style
        const elements = ['floating-panel-component', 'fieldset.variant-option'];
        const ids = ['account-popover'];

        for (const element of elements) {
          if (oldNode.matches(element) && newNode.matches(element)) {
            const oldStyle = oldNode.getAttribute('style');
            if (oldStyle) newNode.setAttribute('style', oldStyle);
          }
        }
        for (const id of ids) {
          if (oldNode.id === id && newNode.id === id) {
            const oldStyle = oldNode.getAttribute('style');
            if (oldStyle) newNode.setAttribute('style', oldStyle);
          }
        }

        // Preserve temporary view transition name
        if (oldNode instanceof HTMLElement && newNode instanceof HTMLElement && oldNode.style.viewTransitionName) {
          newNode.style.viewTransitionName = oldNode.style.viewTransitionName;
        }
      }
    },
    onAfterUpdate(node) {
      if (node instanceof Component) {
        queueMicrotask(() => node.updatedCallback());
      }
    },
  };

  /**
   * Morphs one DOM tree into another by comparing nodes and applying minimal changes
   * @param {Node} oldTree - The existing DOM tree
   * @param {Node | string} newTree - The new DOM tree to morph to
   * @param {Options} [options] - Configuration options
   * @returns {Node} The morphed DOM tree
   */
  function morph(oldTree, newTree, options = MORPH_OPTIONS) {
    if (!oldTree || !newTree) {
      throw new Error('Both oldTree and newTree must be provided');
    }

    if (typeof newTree === 'string') {
      const parsedNewTree = new DOMParser().parseFromString(newTree, 'text/html').body.firstChild;
      if (!parsedNewTree) {
        throw new Error('newTree string is not valid HTML');
      }
      newTree = parsedNewTree;
    }

    if (options.hydrationMode && oldTree instanceof Element && newTree instanceof Element) {
      morphHydrationByKey(oldTree, newTree, options);
      return oldTree;
    }

    if (options.childrenOnly) {
      updateChildren(newTree, oldTree, options);
      return oldTree;
    }

    if (newTree.nodeType === 11) {
      throw new Error('newTree should have one root node (not a DocumentFragment)');
    }

    return walk(newTree, oldTree, options);
  }

  /**
   * Collect targets under a root element that have a non-empty key for the attribute.
   * Includes the root itself if it matches.
   *
   * @param {Element} root
   * @returns {Element[]}
   */
  function collectHydrationTargets(root) {
    const targets = [];
    if (root.hasAttribute(HYDRATION_KEY_ATTRIBUTE)) targets.push(root);
    targets.push(...root.querySelectorAll(`[${HYDRATION_KEY_ATTRIBUTE}]`));
    return targets;
  }

  /**
   * Morph only keyed targets from `newRoot` into `oldRoot` (a.k.a. "keyed lazy hydration").
   *
   * Philosophy:
   * - This updates the *contents* of pre-existing targets. We intentionally do NOT insert new targets (or remove missing ones).
   * - By requiring targets to already exist in `oldRoot`, we preserve runtime state that may already
   *   be attached to the existing DOM (custom elements, listeners, focus, transient UI state) and preserve the layout and UI behavior.
   * - Intended use-case: avoid expensive server-side rendering operations in the initial render, and hydrate targeted sections after page load. e.g. Querying all product and collection drops in off-screen menus.
   *
   * Contract:
   * - An element is eligible only if it has `data-hydration-key="<non-empty>"`.
   * - Matching uses ONLY that key value (no fallbacks) to avoid accidental cross-updates.
   * - Once a target is matched, we run a normal morph *within that target* (attributes + children).
   *
   * @param {Element} oldRoot
   * @param {Element} newRoot
   * @param {Options} options
   */
  function morphHydrationByKey(oldRoot, newRoot, options) {
    const oldTargets = collectHydrationTargets(oldRoot);
    const newTargets = collectHydrationTargets(newRoot);

    /** @type {Map<string, Element[]>} */
    const oldTargetsByKey = new Map();

    for (const oldTarget of oldTargets) {
      const key = oldTarget.getAttribute(HYDRATION_KEY_ATTRIBUTE);
      if (key == null || key === '') continue;

      const existing = oldTargetsByKey.get(key) ?? [];
      existing.push(oldTarget);
      oldTargetsByKey.set(key, existing);
    }

    for (const newTarget of newTargets) {
      const key = newTarget.getAttribute(HYDRATION_KEY_ATTRIBUTE);
      if (key == null || key === '') continue;

      const matches = oldTargetsByKey.get(key);
      const oldTarget = matches?.shift();
      if (!oldTarget) continue;

      // For keyed targets we want attribute updates as well, regardless of the caller's childrenOnly default.
      morph(oldTarget, newTarget, {
        ...options,
        hydrationMode: false,
        childrenOnly: false,
      });
    }
  }

  /**
   * Walk and morph a dom tree
   * @param {Node} newNode - The new node to morph to
   * @param {Node} oldNode - The old node to morph from
   * @param {Options} options - The options object
   * @returns {Node} The new node or the morphed old node
   */
  function walk(newNode, oldNode, options) {
    // Skip morphing if there is no old or new node
    if (!oldNode) return newNode;
    if (!newNode) return oldNode;

    // Skip morphing if nodes are identical
    if (newNode.isSameNode?.(oldNode)) return oldNode;

    // Check node type and tag name first
    if (newNode.nodeType !== oldNode.nodeType) return newNode;
    if (newNode instanceof Element && oldNode instanceof Element) {
      // Skip morphing if the node is shopify-accelerated-checkout-cart https://shopify.dev/docs/storefronts/themes/pricing-payments/accelerated-checkout#implement-accelerated-checkout-buttons-on-cart
      if (oldNode.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT-CART') return oldNode;

      if (newNode.tagName !== oldNode.tagName) return newNode;

      // Only check keys for elements, and only if both nodes have keys
      const newKey = getNodeKey(newNode, options);
      const oldKey = getNodeKey(oldNode, options);
      if (newKey && oldKey && newKey !== oldKey) return newNode;
    }

    // We can morph, update the node and its children
    if (
      oldNode instanceof Element &&
      oldNode.hasAttribute('data-skip-node-update') &&
      newNode instanceof Element &&
      newNode.hasAttribute('data-skip-node-update')
    ) {
      // This is a special case where we don't want to morph the node, but we want to morph the children
      updateChildren(newNode, oldNode, options);
    } else {
      updateNode(newNode, oldNode, options);
      updateChildren(newNode, oldNode, options);
    }

    options.onAfterUpdate?.(newNode);

    return oldNode;
  }

  /**
   * Core morphing function that updates attributes and special elements
   * @param {Node} newNode - Source node with desired state
   * @param {Node} oldNode - Target node to update
   * @param {Options} options - The options object
   */
  function updateNode(newNode, oldNode, options) {
    options.onBeforeUpdate?.(oldNode, newNode);

    if (
      (newNode instanceof HTMLDetailsElement && oldNode instanceof HTMLDetailsElement) ||
      (newNode instanceof HTMLDialogElement && oldNode instanceof HTMLDialogElement)
    ) {
      if (!newNode.hasAttribute('declarative-open')) {
        newNode.open = oldNode.open;
      }
    }

    if (oldNode instanceof HTMLElement && newNode instanceof HTMLElement) {
      for (const attr of ['slot', 'sizes']) {
        const oldValue = oldNode.getAttribute(attr);
        const newValue = newNode.getAttribute(attr);

        if (oldValue !== newValue) {
          oldValue == null ? newNode.removeAttribute(attr) : newNode.setAttribute(attr, oldValue);
        }
      }
    }

    if (newNode instanceof Element && oldNode instanceof Element) {
      if (!oldNode.isEqualNode(newNode)) {
        copyAttributes(newNode, oldNode);
      }
    } else if (newNode instanceof Text || newNode instanceof Comment) {
      if (oldNode.nodeValue !== newNode.nodeValue) {
        oldNode.nodeValue = newNode.nodeValue;
      }
    }

    // Handle special elements
    if (newNode instanceof HTMLInputElement && oldNode instanceof HTMLInputElement) {
      updateInput(newNode, oldNode);
    } else if (newNode instanceof HTMLOptionElement && oldNode instanceof HTMLOptionElement) {
      updateAttribute(newNode, oldNode, 'selected');
    } else if (newNode instanceof HTMLTextAreaElement && oldNode instanceof HTMLTextAreaElement) {
      updateTextarea(newNode, oldNode);
    }
  }

  /**
   * Gets a node's key using the getNodeKey option if provided
   * @param {Node | undefined} node - The node to get the key from
   * @param {Options} [options] - The options object that may contain getNodeKey
   * @returns {string|number|undefined} The node's key if one exists
   */
  function getNodeKey(node, options) {
    return options?.getNodeKey?.(node) ?? (node instanceof Element ? node.id : undefined);
  }

  /**
   * Updates a boolean attribute and its corresponding property on an element
   * @param {any} newNode - The new element
   * @param {any} oldNode - The existing element to update
   * @param {string} name - The name of the attribute/property to update
   */
  function updateAttribute(newNode, oldNode, name) {
    if (newNode[name] !== oldNode[name]) {
      oldNode[name] = newNode[name];
      if (newNode[name] != null) {
        oldNode.setAttribute(name, '');
      } else {
        oldNode.removeAttribute(name);
      }
    }
  }

  /**
   * Copies attributes from a new node to an old node, handling namespaced attributes
   * @param {Element} newNode - The new node to copy attributes from
   * @param {Element} oldNode - The existing node to update attributes on
   */
  function copyAttributes(newNode, oldNode) {
    const oldAttrs = oldNode.attributes;
    const newAttrs = newNode.attributes;

    // Update or add new attributes
    for (const attr of Array.from(newAttrs)) {
      const { name: attrName, namespaceURI: attrNamespaceURI, value: attrValue } = attr;
      const localName = attr.localName || attrName;

      if (attrName === 'src' || attrName === 'href' || attrName === 'srcset' || attrName === 'poster') {
        // Skip updating resource attributes when the value hasn't changed
        // to prevent unnecessary network requests
        if (oldNode.getAttribute(attrName) === attrValue) continue;
      }

      if (attrNamespaceURI) {
        const fromValue = oldNode.getAttributeNS(attrNamespaceURI, localName);
        if (fromValue !== attrValue) {
          oldNode.setAttributeNS(attrNamespaceURI, localName, attrValue);
        }
      } else {
        if (!oldNode.hasAttribute(attrName)) {
          oldNode.setAttribute(attrName, attrValue);
        } else {
          const fromValue = oldNode.getAttribute(attrName);
          if (fromValue !== attrValue) {
            if (attrValue === 'null' || attrValue === 'undefined') {
              oldNode.removeAttribute(attrName);
            } else {
              oldNode.setAttribute(attrName, attrValue);
            }
          }
        }
      }
    }

    // Remove old attributes not present in new node
    for (const attr of Array.from(oldAttrs)) {
      if (attr.specified === false) continue;

      const { name: attrName, namespaceURI: attrNamespaceURI } = attr;
      const localName = attr.localName || attrName;

      if (attrNamespaceURI) {
        if (!newNode.hasAttributeNS(attrNamespaceURI, localName)) {
          oldNode.removeAttributeNS(attrNamespaceURI, localName);
        }
      } else if (!newNode.hasAttribute(attrName)) {
        oldNode.removeAttribute(attrName);
      }
    }
  }

  /**
   * Updates special properties and attributes on input elements
   * Handles checked, disabled, indeterminate states and value
   * @param {HTMLInputElement} newNode - The new input element
   * @param {HTMLInputElement} oldNode - The existing input element to update
   */
  function updateInput(newNode, oldNode) {
    const newValue = newNode.value;

    updateAttribute(newNode, oldNode, 'checked');
    updateAttribute(newNode, oldNode, 'disabled');

    // Handle indeterminate state (cannot be set via HTML attribute)
    if (newNode.indeterminate !== oldNode.indeterminate) {
      oldNode.indeterminate = newNode.indeterminate;
    }

    // Skip file inputs since they can't be changed programmatically
    if (oldNode.type === 'file') return;

    if (newValue !== oldNode.value) {
      oldNode.setAttribute('value', newValue);
      oldNode.value = newValue;
    }

    if (newValue === 'null') {
      oldNode.value = '';
      oldNode.removeAttribute('value');
    }

    if (!newNode.hasAttributeNS(null, 'value')) {
      oldNode.removeAttribute('value');
    } else if (oldNode.type === 'range') {
      // Update range input UI
      oldNode.value = newValue;
    }
  }

  /**
   * Updates the value of a textarea element
   * @param {HTMLTextAreaElement} newNode - The new textarea element
   * @param {HTMLTextAreaElement} oldNode - The existing textarea element to update
   */
  function updateTextarea(newNode, oldNode) {
    const newValue = newNode.value;
    if (newValue !== oldNode.value) {
      oldNode.value = newValue;
    }

    const firstChild = oldNode.firstChild;
    if (firstChild?.nodeType === Node.TEXT_NODE) {
      if (newValue === '' && firstChild.nodeValue === oldNode.placeholder) {
        return;
      }
      firstChild.nodeValue = newValue;
    }
  }

  /**
   * If app scripts store references to the DOM on initialization, they will be invalidated by the morph because browsers don't re-execute them.
   * This function removes and recreates them to force re-execution.
   * @param {Element} container - The container element to search for app block scripts
   */
  function recreateAppBlockScripts(container) {
    const scripts = container.querySelectorAll('.shopify-app-block script[src]');

    for (const script of scripts) {
      if (!(script instanceof HTMLScriptElement)) continue;

      const parent = script.parentElement;
      if (!parent) continue;

      const newScript = document.createElement('script');
      for (const attr of Array.from(script.attributes)) {
        newScript.setAttribute(attr.name, attr.value);
      }
      if (script.textContent) {
        newScript.textContent = script.textContent;
      }

      script.remove();
      parent.appendChild(newScript);
    }
  }

  /**
   * Update the children of elements
   * @param {Node} newNode - The new node to update children on
   * @param {Node} oldNode - The existing node to update children on
   * @param {Options} options - The options object
   */
  function updateChildren(newNode, oldNode, options) {
    if (
      oldNode instanceof Element &&
      oldNode.hasAttribute('data-skip-subtree-update') &&
      newNode instanceof Element &&
      newNode.hasAttribute('data-skip-subtree-update')
    ) {
      return;
    }

    let oldChild, newChild, morphed, oldMatch;
    let offset = 0;

    for (let i = 0; ; i++) {
      oldChild = oldNode.childNodes[i];
      newChild = newNode.childNodes[i - offset];

      // Both nodes are empty, do nothing
      if (!oldChild && !newChild) {
        break;
      }

      // There is no new child, remove old
      if (!newChild) {
        oldChild && oldNode.removeChild(oldChild);
        i--;
        continue;
      }

      // There is no old child, add new
      if (!oldChild) {
        oldNode.appendChild(newChild);
        offset++;
        continue;
      }

      // Both nodes are the same, morph
      if (same(newChild, oldChild, options)) {
        morphed = walk(newChild, oldChild, options);
        if (morphed !== oldChild) {
          oldNode.replaceChild(morphed, oldChild);
          offset++;
        }
        continue;
      }

      if (options.reject?.(oldChild, newChild)) {
        newNode.removeChild(newChild);
        i--;
        continue;
      }

      // Try to find a matching node to reorder
      oldMatch = null;
      for (let j = i; j < oldNode.childNodes.length; j++) {
        const potentialOldNode = oldNode.childNodes[j];

        if (potentialOldNode && same(potentialOldNode, newChild, options)) {
          oldMatch = potentialOldNode;
          break;
        }
      }

      if (oldMatch) {
        morphed = walk(newChild, oldMatch, options);
        if (morphed !== oldMatch) offset++;
        oldNode.insertBefore(morphed, oldChild);
      } else if (!getNodeKey(newChild, options) && !getNodeKey(oldChild, options)) {
        morphed = walk(newChild, oldChild, options);
        if (morphed !== oldChild) {
          oldNode.replaceChild(morphed, oldChild);
          offset++;
        }
      } else {
        oldNode.insertBefore(newChild, oldChild);
        offset++;
      }
    }

    // Recreate app block scripts to bypass browser script deduplication
    if (oldNode instanceof Element) {
      recreateAppBlockScripts(oldNode);
    }
  }

  /**
   * Check if two nodes are the same
   * @param {Node} a - The first node
   * @param {Node} b - The second node
   * @param {Options} options - The options object
   * @returns {boolean} True if the nodes are the same, false otherwise
   */
  function same(a, b, options) {
    // If node types don't match, they're not the same
    if (a.nodeType !== b.nodeType) return false;

    // For elements, check tag name first
    if (a.nodeType === Node.ELEMENT_NODE) {
      if (a instanceof Element && b instanceof Element && a.tagName !== b.tagName) return false;

      // Only compare keys if both nodes have them
      const aKey = getNodeKey(a, options);
      const bKey = getNodeKey(b, options);
      if (aKey && bKey && aKey !== bKey) return false;
    }

    // For text/comment nodes, compare content
    if (a.nodeType === Node.TEXT_NODE && b.nodeType === Node.TEXT_NODE)
      // Trim whitespace to avoid false negatives
      return a.nodeValue?.trim() === b.nodeValue?.trim();
    if (a.nodeType === Node.COMMENT_NODE && b.nodeType === Node.COMMENT_NODE) return a.nodeValue === b.nodeValue;

    // If we get here and nodes are elements with same tag (and compatible keys), they're the same
    return true;
  }


  // ===== section-renderer.js =====

  /**
   * A class to re-render sections using the Section Rendering API
   */
  class SectionRenderer {
    /**
     * The cache of section HTML
     * @type {Map<string, string>}
     */
    #cache = new Map();

    /**
     * The abort controllers by section ID
     * @type {Map<string, AbortController>}
     */
    #abortControllersBySectionId = new Map();

    /**
     * The pending promises
     * @type {Map<string, Promise<string>>}
     */
    #pendingPromises = new Map();

    constructor() {
      window.addEventListener('load', this.#cachePageSections.bind(this));
    }

    /**
     * Renders a section
     * @param {string} sectionId - The section ID
     * @param {Object} [options] - The options
     * @param {boolean} [options.cache] - Whether to use the cache
     * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM
     * @param {URL} [options.url] - The URL to render the section from
     * @returns {Promise<string>} The rendered section HTML
     */
    async renderSection(sectionId, options) {
      const { cache = !Shopify.designMode, mode = 'full' } = options ?? {};
      const { url } = options ?? {};
      this.#abortPendingMorph(sectionId);

      const abortController = new AbortController();
      this.#abortControllersBySectionId.set(sectionId, abortController);

      const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

      if (!abortController.signal.aborted) {
        this.#abortControllersBySectionId.delete(sectionId);

        morphSection(sectionId, sectionHTML, mode);
      }

      return sectionHTML;
    }

    /**
     * Aborts an existing morph for a section
     * @param {string} sectionId - The section ID
     */
    #abortPendingMorph(sectionId) {
      const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
      if (existingAbortController) {
        existingAbortController.abort();
      }
    }

    /**
     * Gets the HTML for a section
     * @param {string} sectionId - The section ID
     * @param {boolean} useCache - Whether to use the cache
     * @param {URL} url - The URL to render the section for
     * @returns {Promise<string>} The rendered section HTML
     */
    async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
      const sectionUrl = buildSectionRenderingURL(sectionId, url);

      let pendingPromise = this.#pendingPromises.get(sectionUrl);
      if (pendingPromise) return pendingPromise;

      if (useCache) {
        const cachedHTML = this.#cache.get(sectionUrl);

        if (cachedHTML) return cachedHTML;
      }

      pendingPromise = fetch(sectionUrl).then((response) => {
        return response.text();
      });

      this.#pendingPromises.set(sectionUrl, pendingPromise);

      const sectionHTML = await pendingPromise;
      this.#pendingPromises.delete(sectionUrl);

      this.#cache.set(sectionUrl, sectionHTML);
      return sectionHTML;
    }

    /**
     * Caches the page sections
     */
    #cachePageSections() {
      for (const section of document.querySelectorAll('.shopify-section')) {
        const url = buildSectionRenderingURL(section.id);
        if (this.#cache.get(url)) return;
        if (containsShadowRoot(section)) return;

        this.#cache.set(url, section.outerHTML);
      }
    }
  }

  const SECTION_ID_PREFIX = 'shopify-section-';

  /**
   * Builds a section rendering URL
   * @param {string} sectionId - The section ID
   * @param {URL} url - The URL to render the section for
   * @returns {string} The section rendering URL
   */
  function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
    url.searchParams.set('section_id', normalizeSectionId(sectionId));
    url.searchParams.sort();

    return url.toString();
  }

  /**
   * Builds a section selector
   * @param {string} sectionId - The section ID
   * @returns {string} The section selector
   */
  function buildSectionSelector(sectionId) {
    return `${SECTION_ID_PREFIX}${sectionId}`;
  }

  /**
   * Normalizes a section ID
   * @param {string} sectionId - The section ID
   * @returns {string} The normalized section ID
   */
  function normalizeSectionId(sectionId) {
    return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
  }

  /**
   * Checks if an element contains a shadow root
   * @param {Element} element - The element to check
   * @returns {boolean} Whether the element contains a shadow root
   */
  function containsShadowRoot(element) {
    return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
  }

  /**
   * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
   */

  /**
   * Morphs the existing section element with the new section contents
   *
   * @param {string} sectionId - The section ID
   * @param {string} html - The new markup the section should morph into
   * @param {'hydration'|'full'} [mode] - Which parts of the section to morph into the DOM. 'hydration' will only morph nodes with `data-hydration-key` attributes.
   */
  async function morphSection(sectionId, html, mode = 'full') {
    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const existingElement = document.getElementById(buildSectionSelector(sectionId));
    const newElement = fragment.getElementById(buildSectionSelector(sectionId));

    if (!existingElement) {
      throw new Error(`Section ${sectionId} not found`);
    }

    if (!newElement) {
      throw new Error(`Section ${sectionId} not found in the section rendering response`);
    }

    morph(existingElement, newElement, {
      ...MORPH_OPTIONS,
      hydrationMode: mode === 'hydration',
    });
  }

  const sectionRenderer = new SectionRenderer();


  // ===== dialog.js =====


  /**
   * A custom element that manages a dialog.
   *
   * @typedef {object} Refs
   * @property {HTMLDialogElement} dialog – The dialog element.
   *
   * @extends Component<Refs>
   */
  class DialogComponent extends Component {
    requiredRefs = ['dialog'];

    connectedCallback() {
      super.connectedCallback();

      if (this.minWidth || this.maxWidth) {
        window.addEventListener('resize', this.#handleResize);
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      if (this.minWidth || this.maxWidth) {
        window.removeEventListener('resize', this.#handleResize);
      }
    }

    #handleResize = debounce(() => {
      const { minWidth, maxWidth } = this;

      if (!minWidth && !maxWidth) return;

      const windowWidth = window.innerWidth;
      if (windowWidth < minWidth || windowWidth > maxWidth) {
        this.closeDialog();
      }
    }, 50);

    #previousScrollY = 0;

    /**
     * Shows the dialog.
     */
    showDialog() {
      const { dialog } = this.refs;

      if (dialog.open) return;

      const scrollY = window.scrollY;
      this.#previousScrollY = scrollY;

      // Prevent layout thrashing by separating DOM reads from DOM writes
      requestAnimationFrame(() => {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;

        dialog.showModal();
        this.dispatchEvent(new DialogOpenEvent());

        this.addEventListener('click', this.#handleClick);
        this.addEventListener('keydown', this.#handleKeyDown);
      });
    }

    /**
     * Closes the dialog.
     */
    closeDialog = async () => {
      const { dialog } = this.refs;

      if (!dialog.open) return;

      this.removeEventListener('click', this.#handleClick);
      this.removeEventListener('keydown', this.#handleKeyDown);

      // Force browser to restart animation by resetting it
      // Temporarily remove any existing animation state
      dialog.style.animation = 'none';

      // Force a reflow
      void dialog.offsetWidth;

      // Now add the closing class and restore animation
      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';

      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });

      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

      dialog.close();
      dialog.classList.remove('dialog-closing');

      this.dispatchEvent(new DialogCloseEvent());
    };

    /**
     * Toggles the dialog.
     */
    toggleDialog = () => {
      if (this.refs.dialog.open) {
        this.closeDialog();
      } else {
        this.showDialog();
      }
    };

    /**
     * Closes the dialog when the user clicks outside of it.
     *
     * @param {MouseEvent} event - The mouse event.
     */
    #handleClick(event) {
      const { dialog } = this.refs;

      if (isClickedOutside(event, dialog)) {
        this.closeDialog();
      }
    }

    /**
     * Closes the dialog when the user presses the escape key.
     *
     * @param {KeyboardEvent} event - The keyboard event.
     */
    #handleKeyDown(event) {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      this.closeDialog();
    }

    /**
     * Gets the minimum width of the dialog.
     *
     * @returns {number} The minimum width of the dialog.
     */
    get minWidth() {
      return Number(this.getAttribute('dialog-active-min-width'));
    }

    /**
     * Gets the maximum width of the dialog.
     *
     * @returns {number} The maximum width of the dialog.
     */
    get maxWidth() {
      return Number(this.getAttribute('dialog-active-max-width'));
    }
  }

  if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

  class DialogOpenEvent extends CustomEvent {
    constructor() {
      super(DialogOpenEvent.eventName);
    }

    static eventName = 'dialog:open';
  }

  class DialogCloseEvent extends CustomEvent {
    constructor() {
      super(DialogCloseEvent.eventName);
    }

    static eventName = 'dialog:close';
  }

  document.addEventListener(
    'toggle',
    (event) => {
      if (event.target instanceof HTMLDetailsElement) {
        if (event.target.hasAttribute('scroll-lock')) {
          const { open } = event.target;
          if (open) {
            document.documentElement.setAttribute('scroll-lock', '');
          } else {
            document.documentElement.removeAttribute('scroll-lock');
          }
        }
      }
    },
    { capture: true }
  );


  // ===== scrolling.js =====

  /**
   * Timeout duration (in milliseconds) after which scroll is considered to have ended.
   * @constant {number}
   */
  const SCROLL_END_TIMEOUT = 50;

  /**
   * Class representing a Scroller that handles smooth scrolling and detects scroll end events.
   *
   * @class
   */
  class Scroller {
    /**
     * The element to apply scrolling to.
     * @type {HTMLElement}
     */
    element;

    /**
     * Promise that resolves when scrolling ends.
     * @type {Promise<void>|undefined}
     */
    #promise = undefined;

    /**
     * Function to resolve the scroll end promise.
     * @type {Function|undefined}
     */
    #resolve = undefined;

    /**
     * Callback function to call while user is scrolling, throttled every 50ms.
     * @type {() => void}
     */
    #throttledCallback;

    /**
     * Callback function invoked after user scroll ends.
     * @type {() => void}
     */
    #endCallback;

    /**
     * Callback function invoked when scrolling starts, regardless of whether it was triggered by a user event.
     * @type {(() => void) | undefined}
     */
    #onScrollInit;

    /**
     * Callback function invoked when scrolling ends, regardless of whether it was triggered by a user event.
     * @type {(() => void) | undefined}
     */
    #onScrollEnd;

    /**
     * Whether the scroll was triggered by a user event.
     * @type {boolean}
     */
    #userEvent = true;

    /**
     * Whether the next scroll event should be ignored.
     * @type {boolean}
     */
    #ignore = false;

    /**
     * Whether the element is currently scrolling.
     * @type {boolean}
     */
    #isScrolling = false;

    /**
     * Creates a Scroller instance.
     *
     * @param {HTMLElement} element - The element to apply scrolling to.
     * @param {Object} options - The options for the scroller.
     * @param {() => void} options.onScroll - Function to call while scrolling and after scrolling ends.
     * @param {() => void} [options.onScrollStart] - Function to call when scrolling starts.
     * @param {() => void} [options.onScrollEnd] - Function to call after scrolling ends.
     */
    constructor(element, options) {
      this.#throttledCallback = throttle(options.onScroll, SCROLL_END_TIMEOUT);
      this.#endCallback = options.onScroll;

      this.#onScrollInit = options.onScrollStart;
      this.#onScrollEnd = options.onScrollEnd;

      this.element = element;
      this.element.addEventListener('scroll', this.#handleScroll);
    }

    /**
     * Scrolls to a specific position or element.
     * @param {number | HTMLElement} input - The position in pixels or an element to scroll to.
     * @param {Object} [options] - Options for the scroll.
     * @param {boolean} [options.instant] - Whether to scroll instantly.
     */
    async to(input, options) {
      let value;

      if (input instanceof HTMLElement) {
        const paddingStart = calculatePaddingStart(this.element, this.axis);
        value = input[`offset${this.#edge}`] - paddingStart;
      } else {
        value = input;
      }

      const currentPosition = this.element[`scroll${this.#edge}`];
      const willChange = currentPosition !== value;

      if (willChange) {
        this.#scroll({ ...options, method: 'scrollTo', value });
      } else if (this.#isScrolling) {
        // If the scroll has started but then it's released in the same original position,
        // the scroll event will not fire, so we need to manually trigger the scroll end.
        this.#handleScrollEnd(false);
      }
    }

    /**
     * Scrolls by a certain number of pixels.
     * @param {number} value - The number of pixels to scroll by.
     * @param {Object} [options] - Options for the scroll.
     * @param {boolean} [options.instant] - Whether to scroll instantly.
     */
    by(value, options) {
      this.#scroll({ ...options, method: 'scrollBy', value });
    }

    /**
     * Scrolls the element.
     * @param {Object} options - The options for the scroll.
     * @param {'scrollTo' | 'scrollBy'} options.method - The method to use to scroll.
     * @param {number} options.value - The value to scroll to.
     * @param {boolean} [options.instant] - Whether to scroll instantly.
     */
    #scroll(options) {
      const { method, value, instant = prefersReducedMotion() } = options;

      this.#reset();
      this.#ignore = instant;
      this.#userEvent = false;

      // Check if we need to scroll at all
      const currentPosition = this.element[`scroll${this.#edge}`];
      const targetPosition = method === 'scrollBy' ? currentPosition + value : value;
      const scrollDistance = Math.abs(targetPosition - currentPosition);

      // If the distance is negligible, don't scroll and resolve immediately
      if (scrollDistance < 1) {
        return Promise.resolve();
      }

      if (!instant) this.#setup();

      this.element[method]({
        [this.#edge.toLowerCase()]: value,
        behavior: instant ? 'instant' : 'smooth',
      });
    }

    /**
     * Gets the scrolling axis ('x' or 'y') based on the element's dimensions.
     * @type {'x' | 'y'}
     * @readonly
     */
    get axis() {
      return getScrollAxis(this.element);
    }

    /**
     * Promise that resolves when scrolling ends.
     * @type {Promise<void>}
     * @readonly
     */
    get finished() {
      return this.#promise ?? Promise.resolve();
    }

    /**
     * Gets the scroll edge property ('Left' or 'Top') based on the axis.
     * @returns {'Left' | 'Top'}
     */
    get #edge() {
      return this.axis === 'x' ? 'Left' : 'Top';
    }

    /**
     * Sets up the scroll end promise if not already set.
     */
    #setup() {
      if (this.#promise) {
        return;
      }

      this.#promise = new Promise((resolve) => (this.#resolve = resolve));
    }

    #reset = () => {
      this.#handleScrollEnd.cancel();
      this.#resolve?.();

      this.#promise = undefined;
      this.#resolve = undefined;
      this.#userEvent = true;
      this.#ignore = false;
    };

    /**
     * Event handler for the 'scroll' event.
     */
    #handleScroll = () => {
      if (!this.#isScrolling) {
        this.#onScrollInit?.();
        this.#isScrolling = true;
      }

      if (this.#ignore) {
        this.#reset();
        this.#handleScrollEnd(false);
        return;
      }

      const userEvent = this.#userEvent;

      this.#setup();
      if (userEvent) this.#throttledCallback();
      this.#handleScrollEnd(userEvent);
    };

    /**
     * Handler called when scrolling has ended.
     */
    #handleScrollEnd = debounce(
      /**
       * @param {boolean} userEvent
       */
      (userEvent) => {
        this.#resolve?.();
        if (userEvent) this.#endCallback();
        this.#reset();

        if (this.#isScrolling) {
          this.#onScrollEnd?.();
          this.#isScrolling = false;
        }
      },
      SCROLL_END_TIMEOUT
    );

    /**
     * Sets the scroll snap behavior of the element.
     * @param {boolean} value - Whether to enable scroll snap.
     */
    set snap(value) {
      // Changing the snap behavior will trigger a scroll event, which we should ignore
      this.#ignore = true;
      this.element.style.setProperty('scroll-snap-type', value ? `${this.axis} mandatory` : 'none');
    }

    /**
     * Destroys the Scroller instance.
     */
    destroy() {
      this.element.removeEventListener('scroll', this.#handleScroll);
    }
  }

  /**
   * Gets the scroll axis ('x' or 'y') based on the element's dimensions.
   * @param {HTMLElement} el - The element to get the scroll axis of.
   * @returns {'x' | 'y'}
   */
  function getScrollAxis(el) {
    if (el.scrollHeight > el.clientHeight && el.scrollWidth === el.clientWidth) {
      return 'y';
    }

    if (el.scrollWidth > el.clientWidth && el.scrollHeight === el.clientHeight) {
      return 'x';
    }

    return el.scrollWidth > el.scrollHeight ? 'x' : 'y';
  }

  /**
   * Calculates the padding-start around an element to update the scroll offset.
   * @param {HTMLElement} element - The element to calculate the padding-start of.
   * @param {'x' | 'y'} axis - The axis to calculate the padding-start of.
   * @returns {number} The padding-start in pixels.
   */
  function calculatePaddingStart(element, axis) {
    const computedStyle = getComputedStyle(element);
    const value = axis === 'x' ? computedStyle.paddingInlineStart : computedStyle.paddingBlockStart;

    return parseFloat(value);
  }

  /**
   * Scrolls an element into view.
   * @param {Element} element - The element to scroll into view.
   * @param {Object} options - The options for the scroll.
   * @param {ScrollBehavior} [options.behavior='smooth'] - The behavior of the scroll.
   * @param {'start' | 'center' | 'end'} [options.block='start'] - The block alignment of the element.
   * @param {'start' | 'center' | 'end'} [options.inline='start'] - The inline alignment of the element.
   * @param {Element} [options.ancestor] - The ancestor element to scroll into view.
   */
  function scrollIntoView(element, { ancestor, behavior = 'smooth', block = 'start', inline = 'start' } = {}) {
    if (!ancestor) {
      return element.scrollIntoView({ behavior, block, inline });
    }

    const elemRect = element.getBoundingClientRect();
    const ancestorRect = ancestor.getBoundingClientRect();

    /**
     * Calculates the scroll offset for an element.
     * @param {'start' | 'center' | 'end'} alignment - The alignment of the element.
     * @param {number} ancestorStart - The start of the ancestor element.
     * @param {number} ancestorLength - The length of the ancestor element.
     * @param {number} elemStart - The start of the element.
     * @param {number} elemLength - The length of the element.
     * @param {number} currentScroll - The current scroll position.
     * @returns {number} The scroll offset.
     */
    const calculateScrollOffset = (alignment, ancestorStart, ancestorLength, elemStart, elemLength, currentScroll) => {
      switch (alignment) {
        case 'start':
          return currentScroll + elemStart - ancestorStart;
        case 'center':
          return currentScroll + elemStart - ancestorStart - ancestorLength / 2 + elemLength / 2;
        case 'end':
          return currentScroll + elemStart - ancestorStart - ancestorLength + elemLength;
        default:
          return currentScroll;
      }
    };

    const scrollTop =
      ancestor.scrollHeight > ancestor.clientHeight
        ? calculateScrollOffset(
            block,
            ancestorRect.top,
            ancestor.clientHeight,
            elemRect.top,
            elemRect.height,
            ancestor.scrollTop
          )
        : ancestor.scrollTop;

    const scrollLeft =
      ancestor.scrollWidth > ancestor.clientWidth
        ? calculateScrollOffset(
            inline,
            ancestorRect.left,
            ancestor.clientWidth,
            elemRect.left,
            elemRect.width,
            ancestor.scrollLeft
          )
        : ancestor.scrollLeft;

    ancestor.scrollTo({ top: scrollTop, left: scrollLeft, behavior });
  }

  // Still extends HTMLElement over Component so that refs are still available to parent components (e.g. Slideshow)
  class ScrollHint extends HTMLElement {
    /** @type {number | null} */
    #rafId = null;

    connectedCallback() {
      this.addEventListener('scroll', this.#handleScroll);
      this.#resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this.removeEventListener('scroll', this.#handleScroll);
      this.#resizeObserver.disconnect();
      if (this.#rafId !== null) {
        cancelAnimationFrame(this.#rafId);
        this.#rafId = null;
      }
    }

    #handleScroll = () => {
      // Throttle scroll updates with requestAnimationFrame to avoid layout thrashing
      if (this.#rafId !== null) return;

      this.#rafId = requestAnimationFrame(() => {
        this.#rafId = null;
        this.#update();
      });
    };

    #update = () => {
      const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = this;
      const scrollDirection = scrollWidth > clientWidth ? 'horizontal' : 'vertical';
      const scrollPercentage =
        scrollDirection === 'vertical'
          ? scrollTop / (scrollHeight - clientHeight)
          : scrollLeft / (scrollWidth - clientWidth);

      this.style.maskImage = Number.isNaN(scrollPercentage)
        ? ''
        : `linear-gradient(
          to ${scrollDirection === 'vertical' ? 'bottom' : 'right'},
          transparent ${scrollPercentage > 0 ? 1 : 0}%,
          black ${scrollPercentage < 0.1 ? scrollPercentage * 100 : 10}%,
          black ${scrollPercentage > 0.9 ? scrollPercentage * 100 : 90}%,
          transparent 100%
        )`;
    };

    #resizeObserver = new ResizeObserver(this.#update);
  }

  if (!customElements.get('scroll-hint')) {
    customElements.define('scroll-hint', ScrollHint);
  }


  // ===== accordion-custom.js =====

  // Accordion
  // Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
  class AccordionCustom extends HTMLElement {
    /** @type {HTMLDetailsElement} */
    get details() {
      const details = this.querySelector('details');

      if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

      return details;
    }

    /** @type {HTMLElement} */
    get summary() {
      const summary = this.details.querySelector('summary');

      if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

      return summary;
    }

    get #disableOnMobile() {
      return this.dataset.disableOnMobile === 'true';
    }

    get #disableOnDesktop() {
      return this.dataset.disableOnDesktop === 'true';
    }

    get #closeWithEscape() {
      return this.dataset.closeWithEscape === 'true';
    }

    #controller = new AbortController();

    connectedCallback() {
      const { signal } = this.#controller;

      this.#setDefaultOpenState();

      this.addEventListener('keydown', this.#handleKeyDown, { signal });
      this.summary.addEventListener('click', this.handleClick, { signal });
      mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
    }

    /**
     * Handles the disconnect event.
     */
    disconnectedCallback() {
      // Disconnect all the event listeners
      this.#controller.abort();
    }

    /**
     * Handles the click event.
     * @param {Event} event - The event.
     */
    handleClick = (event) => {
      const isMobile = isMobileBreakpoint();
      const isDesktop = !isMobile;

      // Stop default behaviour from the browser
      if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
        event.preventDefault();
        return;
      }
    };

    /**
     * Handles the media query change event.
     */
    #handleMediaQueryChange = () => {
      this.#setDefaultOpenState();
    };

    /**
     * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
     */
    #setDefaultOpenState() {
      const isMobile = isMobileBreakpoint();

      this.details.open =
        (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
        (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
    }

    /**
     * Handles keydown events for the accordion
     *
     * @param {KeyboardEvent} event - The keyboard event.
     */
    #handleKeyDown(event) {
      // Close the accordion when used as a menu
      if (event.key === 'Escape' && this.#closeWithEscape) {
        event.preventDefault();

        this.details.open = false;
        this.summary.focus();
      }
    }
  }

  if (!customElements.get('accordion-custom')) {
    customElements.define('accordion-custom', AccordionCustom);
  }


  // ===== component-quantity-selector.js =====



  /**
   * A custom element that allows the user to select a quantity.
   *
   * This component follows a pure event-driven architecture where quantity changes
   * are broadcast via QuantitySelectorUpdateEvent. Parent components that contain
   * quantity selectors listen for these events and handle them according to their
   * specific needs, with event filtering ensuring each parent only processes events
   * from its own quantity selectors to prevent conflicts between different cart
   * update strategies.
   *
   * @typedef {Object} Refs
   * @property {HTMLInputElement} quantityInput
   * @property {HTMLButtonElement} minusButton
   * @property {HTMLButtonElement} plusButton
   *
   * @extends {Component<Refs>}
   */
  class QuantitySelectorComponent extends Component {
    requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
    serverDisabledMinus = false;
    serverDisabledPlus = false;
    initialized = false;

    connectedCallback() {
      super.connectedCallback();

      // Capture server-disabled state on first load
      const { minusButton, plusButton } = this.refs;

      if (minusButton.disabled) {
        this.serverDisabledMinus = true;
      }
      if (plusButton.disabled) {
        this.serverDisabledPlus = true;
      }

      this.initialized = true;
      this.updateButtonStates();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
    }

    /**
     * Updates cart quantity and refreshes component state
     * @param {number} cartQty - The quantity currently in cart for this variant
     */
    setCartQuantity(cartQty) {
      this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
      this.updateCartQuantity();
    }

    /**
     * Checks if the current quantity can be added to cart without exceeding max
     * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}} Validation result
     */
    canAddToCart() {
      const { max, cartQuantity, value } = this.getCurrentValues();
      const quantityToAdd = value;
      const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

      return {
        canAdd: !wouldExceedMax,
        maxQuantity: max,
        cartQuantity,
        quantityToAdd,
      };
    }

    /**
     * Gets the current quantity value
     * @returns {string} The current value
     */
    getValue() {
      return this.refs.quantityInput.value;
    }

    /**
     * Sets the current quantity value
     * @param {string} value - The value to set
     */
    setValue(value) {
      this.refs.quantityInput.value = value;
    }

    /**
     * Updates min/max/step constraints and snaps value to valid increment
     * @param {string} min - Minimum value
     * @param {string|null} max - Maximum value (null if no max)
     * @param {string} step - Step increment
     */
    updateConstraints(min, max, step) {
      const { quantityInput } = this.refs;
      const currentValue = parseInt(quantityInput.value) || 0;

      quantityInput.min = min;
      if (max) {
        quantityInput.max = max;
      } else {
        quantityInput.removeAttribute('max');
      }
      quantityInput.step = step;

      const newMin = parseIntOrDefault(min, 1);
      const newStep = parseIntOrDefault(step, 1);
      const effectiveMax = this.getEffectiveMax();

      // Snap to valid increment if not already aligned
      let newValue = currentValue;
      if ((currentValue - newMin) % newStep !== 0) {
        // Snap DOWN to closest valid increment
        newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
      }

      // Ensure value is within bounds
      newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));

      if (newValue !== currentValue) {
        quantityInput.value = newValue.toString();
      }

      this.updateButtonStates();
    }

    /**
     * Gets current values from DOM (fresh read every time)
     * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
     */
    getCurrentValues() {
      const { quantityInput } = this.refs;

      return {
        min: parseIntOrDefault(quantityInput.min, 1),
        max: parseIntOrDefault(quantityInput.max, null),
        step: parseIntOrDefault(quantityInput.step, 1),
        value: parseIntOrDefault(quantityInput.value, 0),
        cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
      };
    }

    /**
     * Gets the effective maximum value for this quantity selector
     * Product page: max - cartQuantity (how many can be added)
     * Override in subclass for different behavior
     * @returns {number | null} The effective max, or null if no max
     */
    getEffectiveMax() {
      const { max, cartQuantity, min } = this.getCurrentValues();
      if (max === null) return null;
      // Product page: can only add what's left
      return Math.max(max - cartQuantity, min);
    }

    /**
     * Updates button states based on current value and limits
     */
    updateButtonStates() {
      const { minusButton, plusButton } = this.refs;
      const { min, value } = this.getCurrentValues();
      const effectiveMax = this.getEffectiveMax();

      // Only manage buttons that weren't server-disabled
      if (!this.serverDisabledMinus) {
        minusButton.disabled = value <= min;
      }

      if (!this.serverDisabledPlus) {
        plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
      }
    }

    /**
     * Updates quantity by a given step
     * @param {number} stepMultiplier - Positive for increase, negative for decrease
     */
    updateQuantity(stepMultiplier) {
      const { quantityInput } = this.refs;
      const { min, step, value } = this.getCurrentValues();
      const effectiveMax = this.getEffectiveMax();

      const newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * stepMultiplier));

      quantityInput.value = newValue.toString();
      this.onQuantityChange();
      this.updateButtonStates();
    }

    /**
     * Handles the quantity increase event.
     * @param {Event} event - The event.
     */
    increaseQuantity(event) {
      if (!(event.target instanceof HTMLElement)) return;
      event.preventDefault();
      this.updateQuantity(1);
    }

    /**
     * Handles the quantity decrease event.
     * @param {Event} event - The event.
     */
    decreaseQuantity(event) {
      if (!(event.target instanceof HTMLElement)) return;
      event.preventDefault();
      this.updateQuantity(-1);
    }

    /**
     * When our input gets focused, we want to fully select the value.
     * @param {FocusEvent} event
     */
    selectInputValue(event) {
      const { quantityInput } = this.refs;
      if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

      quantityInput.select();
    }

    /**
     * Handles the quantity set event (on blur).
     * Validates and snaps to valid values.
     * @param {Event} event - The event.
     */
    setQuantity(event) {
      if (!(event.target instanceof HTMLInputElement)) return;

      event.preventDefault();
      const { quantityInput } = this.refs;
      const { min, step } = this.getCurrentValues();
      const effectiveMax = this.getEffectiveMax();

      // Snap to bounds
      const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, parseInt(event.target.value) || 0));

      // Validate step increment
      if ((quantity - min) % step !== 0) {
        // Set the invalid value and trigger native HTML validation
        quantityInput.value = quantity.toString();
        quantityInput.reportValidity();
        return;
      }

      quantityInput.value = quantity.toString();
      this.onQuantityChange();
      this.updateButtonStates();
    }

    /**
     * Handles the quantity change event.
     */
    onQuantityChange() {
      const { quantityInput } = this.refs;
      const newValue = parseInt(quantityInput.value);

      this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
    }

    /**
     * Updates the cart quantity from data attribute and refreshes button states
     * Called when cart is updated from external sources
     */
    updateCartQuantity() {
      const { quantityInput } = this.refs;
      const { min, value } = this.getCurrentValues();
      const effectiveMax = this.getEffectiveMax();

      // Clamp value to new effective max if necessary
      const clampedValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value));

      if (clampedValue !== value) {
        quantityInput.value = clampedValue.toString();
      }

      this.updateButtonStates();
    }

    /**
     * Gets the quantity input.
     * @returns {HTMLInputElement} The quantity input.
     */
    get quantityInput() {
      if (!this.refs.quantityInput) {
        throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
      }

      return this.refs.quantityInput;
    }
  }

  if (!customElements.get('quantity-selector-component')) {
    customElements.define('quantity-selector-component', QuantitySelectorComponent);
  }


  // ===== cart-icon.js =====



  /**
   * A custom element that displays a cart icon.
   *
   * @typedef {object} Refs
   * @property {HTMLElement} cartBubble - The cart bubble element.
   * @property {HTMLElement} cartBubbleText - The cart bubble text element.
   * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
   *
   * @extends {Component<Refs>}
   */
  class CartIcon extends Component {
    requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

    /** @type {number} */
    get currentCartCount() {
      return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
    }

    set currentCartCount(value) {
      this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
    }

    connectedCallback() {
      super.connectedCallback();

      document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
      window.addEventListener('pageshow', this.onPageShow);
      this.ensureCartBubbleIsCorrect();
    }

    disconnectedCallback() {
      super.disconnectedCallback();

      document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    /**
     * Handles the page show event when the page is restored from cache.
     * @param {PageTransitionEvent} event - The page show event.
     */
    onPageShow = (event) => {
      if (event.persisted) {
        this.ensureCartBubbleIsCorrect();
      }
    };

    /**
     * Handles the cart update event.
     * @param {CartUpdateEvent} event - The cart update event.
     */
    onCartUpdate = async (event) => {
      const itemCount = event.detail.data?.itemCount ?? 0;
      const comingFromProductForm = event.detail.data?.source === 'product-form-component';

      this.renderCartBubble(itemCount, comingFromProductForm);
    };

    /**
     * Renders the cart bubble.
     * @param {number} itemCount - The number of items in the cart.
     * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
     */
    renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
      // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

      this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
      this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

      this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

      this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(this.currentCartCount),
          timestamp: Date.now(),
        })
      );

      if (!animate || itemCount === 0) return;

      // Ensure element is visible before starting animation
      // Use requestAnimationFrame to ensure the browser sees the state change
      await new Promise((resolve) => requestAnimationFrame(resolve));

      this.refs.cartBubble.classList.add('cart-bubble--animating');
      await onAnimationEnd(this.refs.cartBubbleText);

      this.refs.cartBubble.classList.remove('cart-bubble--animating');
    };

    /**
     * Checks if the cart count is correct.
     */
    ensureCartBubbleIsCorrect = () => {
      // Ensure refs are available
      if (!this.refs.cartBubbleCount) return;

      const sessionStorageCount = sessionStorage.getItem('cart-count');

      // If no session storage data, nothing to check
      if (sessionStorageCount === null) return;

      const visibleCount = this.refs.cartBubbleCount.textContent;

      try {
        const { value, timestamp } = JSON.parse(sessionStorageCount);

        // Check if the stored count matches what's visible
        if (value === visibleCount) return;

        // Only update if timestamp is recent (within 10 seconds)
        if (Date.now() - timestamp < 10000) {
          const count = parseInt(value, 10);

          if (count >= 0) {
            this.renderCartBubble(count, false, false);
          }
        }
      } catch (_) {
        // no-op
      }
    };
  }

  if (!customElements.get('cart-icon')) {
    customElements.define('cart-icon', CartIcon);
  }


  // ===== cart-note.js =====



  /**
   * A custom element that displays a cart note.
   */
  class CartNote extends Component {
    /** @type {AbortController | null} */
    #activeFetch = null;

    /**
     * Handles updates to the cart note.
     * @param {InputEvent} event - The input event in our text-area.
     */
    updateCartNote = debounce(async (event) => {
      if (!(event.target instanceof HTMLTextAreaElement)) return;

      const note = event.target.value;
      if (this.#activeFetch) {
        this.#activeFetch.abort();
      }

      const abortController = new AbortController();
      this.#activeFetch = abortController;

      try {
        const config = fetchConfig('json', {
          body: JSON.stringify({ note }),
        });

        await fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal,
        });
      } catch (error) {
      } finally {
        this.#activeFetch = null;
        
      }
    }, 200);
  }

  if (!customElements.get('cart-note')) {
    customElements.define('cart-note', CartNote);
  }


  // ===== cart-discount.js =====





  /**
   * A custom element that applies a discount to the cart.
   *
   * @typedef {Object} CartDiscountComponentRefs
   * @property {HTMLElement} cartDiscountError - The error element.
   * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
   * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
   */

  /**
   * @extends {Component<CartDiscountComponentRefs>}
   */
  class CartDiscount extends Component {
    requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

    /** @type {AbortController | null} */
    #activeFetch = null;

    #createAbortController() {
      if (this.#activeFetch) {
        this.#activeFetch.abort();
      }

      const abortController = new AbortController();
      this.#activeFetch = abortController;
      return abortController;
    }

    /**
     * Handles updates to the cart note.
     * @param {SubmitEvent} event - The submit event on our form.
     */
    applyDiscount = async (event) => {
      const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

      event.preventDefault();
      event.stopPropagation();

      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const discountCode = form.querySelector('input[name="discount"]');
      if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

      const discountCodeValue = discountCode.value;

      const abortController = this.#createAbortController();

      try {
        const existingDiscounts = this.#existingDiscounts();
        if (existingDiscounts.includes(discountCodeValue)) return;

        cartDiscountError.classList.add('hidden');
        cartDiscountErrorDiscountCode.classList.add('hidden');
        cartDiscountErrorShipping.classList.add('hidden');

        const config = fetchConfig('json', {
          body: JSON.stringify({
            discount: [...existingDiscounts, discountCodeValue].join(','),
            sections: [this.dataset.sectionId],
          }),
        });

        const response = await fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal,
        });

        const data = await response.json();

        if (
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === false;
          })
        ) {
          discountCode.value = '';
          this.#handleDiscountError('discount_code');
          return;
        }

        const newHtml = data.sections[this.dataset.sectionId];
        const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
        const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
        const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
        if (section) {
          const codes = Array.from(discountCodes)
            .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
            .filter(Boolean);
          // Before morphing, we need to check if the shipping discount is applicable in the UI
          // we check the liquid logic compared to the cart payload to assess whether we leveraged
          // a valid shipping discount code.
          if (
            codes.length === existingDiscounts.length &&
            codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
            data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
              return discount.code === discountCodeValue && discount.applicable === true;
            })
          ) {
            this.#handleDiscountError('shipping');
            discountCode.value = '';
            return;
          }
        }

        document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
        morphSection(this.dataset.sectionId, newHtml);

        // CART-HACK: morph preserves the <details open> attribute, so the
        // Liquid disclosure_expanded=false has no effect after morphSection.
        // Close it here, blur input, so the input+Apply pair stops rendering.
        const detailsEl = this.querySelector('details');
        if (detailsEl) detailsEl.removeAttribute('open');
        discountCode.blur();
      } catch (error) {
      } finally {
        this.#activeFetch = null;

      }
    };

    /**
     * Handles removing a discount from the cart.
     * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
     */
    removeDiscount = async (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (
        (event instanceof KeyboardEvent && event.key !== 'Enter') ||
        !(event instanceof MouseEvent) ||
        !(event.target instanceof HTMLElement) ||
        typeof this.dataset.sectionId !== 'string'
      ) {
        return;
      }

      const pill = event.target.closest('.cart-discount__pill');
      if (!(pill instanceof HTMLLIElement)) return;

      const discountCode = pill.dataset.discountCode;
      if (!discountCode) return;

      const existingDiscounts = this.#existingDiscounts();
      const index = existingDiscounts.indexOf(discountCode);
      if (index === -1) return;

      existingDiscounts.splice(index, 1);

      const abortController = this.#createAbortController();

      try {
        const config = fetchConfig('json', {
          body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
        });

        const response = await fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal,
        });

        const data = await response.json();

        document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
        morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
      } catch (error) {
      } finally {
        this.#activeFetch = null;
      }
    };

    /**
     * Handles the discount error.
     *
     * @param {'discount_code' | 'shipping'} type - The type of discount error.
     */
    #handleDiscountError(type) {
      const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
      const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
      cartDiscountError.classList.remove('hidden');
      target.classList.remove('hidden');
    }

    /**
     * Returns an array of existing discount codes.
     * @returns {string[]}
     */
    #existingDiscounts() {
      /** @type {string[]} */
      const discountCodes = [];
      const discountPills = this.querySelectorAll('.cart-discount__pill');
      for (const pill of discountPills) {
        if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
          discountCodes.push(pill.dataset.discountCode);
        }
      }

      return discountCodes;
    }
  }

  if (!customElements.get('cart-discount-component')) {
    customElements.define('cart-discount-component', CartDiscount);
  }


  // ===== component-cart-items.js =====





  /** @typedef {import('./utilities').TextComponent} TextComponent */

  /**
   * A custom element that displays a cart items component.
   *
   * @typedef {object} Refs
   * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
   * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
   * @property {TextComponent} cartTotal - The cart total.
   *
   * @extends {Component<Refs>}
   */
  class CartItemsComponent extends Component {
    #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

    connectedCallback() {
      super.connectedCallback();

      document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
      document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
      document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    }

    disconnectedCallback() {
      super.disconnectedCallback();

      document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
      document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    }

    /**
     * Handles QuantitySelectorUpdateEvent change event.
     * @param {QuantitySelectorUpdateEvent} event - The event.
     */
    #onQuantityChange(event) {
      if (!(event.target instanceof Node) || !this.contains(event.target)) return;

      const { quantity, cartLine: line } = event.detail;

      // Cart items require a line number
      if (!line) return;

      if (quantity === 0) {
        return this.onLineItemRemove(line);
      }

      this.updateQuantity({
        line,
        quantity,
        action: 'change',
      });
      const lineItemRow = this.refs.cartItemRows[line - 1];

      if (!lineItemRow) return;

      const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
      textComponent?.shimmer();
    }

    /**
     * Handles the line item removal.
     * @param {number} line - The line item index.
     */
    onLineItemRemove(line) {
      this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });

      const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

      if (!cartItemRowToRemove) return;

      const rowsToRemove = [
        cartItemRowToRemove,
        // Get all nested lines of the row to remove
        ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      ];

      // If the cart item row is the last row, optimistically trigger the cart empty state
      const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

      const template = document.getElementById('empty-cart-template');
      if (isEmptyCart && template instanceof HTMLTemplateElement) {
        const clone = document.importNode(template.content, true);

        startViewTransition(() => {
          this.replaceChildren(clone);
        }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

        return;
      }

      // Add class to the row to trigger the animation
      rowsToRemove.forEach((row) => {
        const remove = () => row.remove();

        if (prefersReducedMotion()) return remove();

        row.style.setProperty('--row-height', `${row.clientHeight}px`);
        row.classList.add('removing');

        // Remove the row after the animation ends
        onAnimationEnd(row, remove);
      });
    }

    /**
     * Updates the quantity.
     * @param {Object} config - The config.
     * @param {number} config.line - The line.
     * @param {number} config.quantity - The quantity.
     * @param {string} config.action - The action.
     */
    updateQuantity(config) {
      const cartPerformaceUpdateMarker = 

      this.#disableCartItems();

      const { line, quantity } = config;
      const { cartTotal } = this.refs;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        line: line,
        quantity: quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      cartTotal?.shimmer();

      fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
        .then((response) => {
          return response.text();
        })
        .then((responseText) => {
          const parsedResponseText = JSON.parse(responseText);

          resetShimmer(this);

          if (parsedResponseText.errors) {
            this.#handleCartError(line, parsedResponseText);
            return;
          }

          const newSectionHTML = new DOMParser().parseFromString(
            parsedResponseText.sections[this.sectionId],
            'text/html'
          );

          // Grab the new cart item count from a hidden element
          const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
          const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

          // Update data-cart-quantity for all matching variants
          this.#updateQuantitySelectors(parsedResponseText);

          this.dispatchEvent(
            new CartUpdateEvent(parsedResponseText, this.sectionId, {
              itemCount: newCartItemCount,
              source: 'cart-items-component',
              sections: parsedResponseText.sections,
            })
          );

          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

          this.#updateCartQuantitySelectorButtonStates();
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.#enableCartItems();
          
        });
    }

    /**
     * Handles the discount update.
     * @param {DiscountUpdateEvent} event - The event.
     */
    handleDiscountUpdate = (event) => {
      this.#handleCartUpdate(event);
    };

    /**
     * Handles the cart error.
     * @param {number} line - The line.
     * @param {Object} parsedResponseText - The parsed response text.
     * @param {string} parsedResponseText.errors - The errors.
     */
    #handleCartError = (line, parsedResponseText) => {
      const quantitySelector = this.refs.quantitySelectors[line - 1];
      const quantityInput = quantitySelector?.querySelector('input');

      if (!quantityInput) throw new Error('Quantity input not found');

      quantityInput.value = quantityInput.defaultValue;

      const cartItemError = this.refs[`cartItemError-${line}`];
      const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

      if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
      if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove('hidden');
    };

    /**
     * Handles the cart update.
     *
     * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
     */
    #handleCartUpdate = (event) => {
      if (event instanceof DiscountUpdateEvent) {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
        return;
      }
      if (event.target === this) return;

      const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
      if (cartItemsHtml) {
        morphSection(this.sectionId, cartItemsHtml);

        // Update button states for all cart quantity selectors after morph
        this.#updateCartQuantitySelectorButtonStates();
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }
    };

    /**
     * Disables the cart items.
     */
    #disableCartItems() {
      this.classList.add('cart-items-disabled');
    }

    /**
     * Enables the cart items.
     */
    #enableCartItems() {
      this.classList.remove('cart-items-disabled');
    }

    /**
     * Updates quantity selectors for all matching variants in the cart.
     * @param {Object} updatedCart - The updated cart object.
     * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
     */
    #updateQuantitySelectors(updatedCart) {
      if (!updatedCart.items) return;

      for (const item of updatedCart.items) {
        const variantId = item.variant_id.toString();
        const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

        for (const selector of selectors) {
          const input = selector.querySelector('input[data-cart-quantity]');
          if (!input) continue;

          input.setAttribute('data-cart-quantity', item.quantity.toString());

          // Update the quantity selector's internal state
          if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
            selector.updateCartQuantity();
          }
        }
      }
    }

    /**
     * Updates button states for all cart quantity selector components.
     */
    #updateCartQuantitySelectorButtonStates() {
      for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
        /** @type {any} */ (selector).updateButtonStates?.();
      }
    }

    /**
     * Gets the section id.
     * @returns {string} The section id.
     */
    get sectionId() {
      const { sectionId } = this.dataset;

      if (!sectionId) throw new Error('Section id missing');

      return sectionId;
    }

    /**
     * @returns {boolean} Whether the component is a drawer.
     */
    get isDrawer() {
      return this.dataset.drawer !== undefined;
    }
  }

  if (!customElements.get('cart-items-component')) {
    customElements.define('cart-items-component', CartItemsComponent);
  }


  // ===== component-cart-quantity-selector.js =====

  /**
   * A custom element that allows the user to select a quantity in the cart.
   * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
   * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
   *
   * @extends {QuantitySelectorComponent}
   */
  class CartQuantitySelectorComponent extends QuantitySelectorComponent {
    /**
     * Gets the effective maximum value for cart quantity selector
     * Cart page: uses absolute max (how much can be in cart total)
     * @returns {number | null} The effective max, or null if no max
     */
    getEffectiveMax() {
      const { max } = this.getCurrentValues();
      return max; // Cart uses absolute max, not max minus cart quantity
    }

    /**
     * Updates button states based on current value and limits
     * Cart buttons are always managed client-side, never server-disabled
     */
    updateButtonStates() {
      const { minusButton, plusButton } = this.refs;
      const { min, value } = this.getCurrentValues();
      const effectiveMax = this.getEffectiveMax();

      // Cart buttons are always dynamically managed
      minusButton.disabled = value <= min;
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }

  if (!customElements.get('cart-quantity-selector-component')) {
    customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
  }


  // ===== cart-drawer.js =====



  /**
   * A custom element that manages a cart drawer.
   *
   * @typedef {object} Refs
   * @property {HTMLDialogElement} dialog - The dialog element.
   *
   * @extends {DialogComponent}
   */
  class CartDrawerComponent extends DialogComponent {
    /** @type {number} */
    #summaryThreshold = 0.5;

    /** @type {AbortController | null} */
    #historyAbortController = null;

    connectedCallback() {
      super.connectedCallback();
      document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
      this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
      this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
      this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

      if (history.state?.cartDrawerOpen) {
        history.replaceState(null, '');
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
      this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
      this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
      this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
      this.#historyAbortController?.abort();
    }

    #handleHistoryOpen = () => {
      if (!isMobileBreakpoint()) return;

      if (!history.state?.cartDrawerOpen) {
        history.pushState({ cartDrawerOpen: true }, '');
      }

      this.#historyAbortController = new AbortController();
      window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
    };

    #handleHistoryClose = () => {
      this.#historyAbortController?.abort();
      if (history.state?.cartDrawerOpen) {
        history.back();
      }
    };

    #handlePopState = async () => {
      if (this.refs.dialog?.open) {
        this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
        await this.closeDialog();
        this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
      }
    };

    #handleCartAdd = () => {
      if (this.hasAttribute('auto-open')) {
        this.showDialog();
      }
    };

    open() {
      this.showDialog();

      /**
       * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
       */
      customElements.whenDefined('shopify-payment-terms').then(() => {
        const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
        const cta = installmentsContent?.querySelector('#shopify-installments-cta');
        cta?.addEventListener('click', this.closeDialog, { once: true });
      });
    }

    close() {
      this.closeDialog();
    }

    #updateStickyState() {
      const { dialog } = /** @type {Refs} */ (this.refs);
      if (!dialog) return;

      // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
      const content = dialog.querySelector('.cart-drawer__content');
      const summary = dialog.querySelector('.cart-drawer__summary');

      if (!content || !summary) {
        // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
        dialog.setAttribute('cart-summary-sticky', 'false');
        return;
      }

      const drawerHeight = dialog.getBoundingClientRect().height;
      const summaryHeight = summary.getBoundingClientRect().height;
      const ratio = summaryHeight / drawerHeight;
      dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
    }
  }

  if (!customElements.get('cart-drawer-component')) {
    customElements.define('cart-drawer-component', CartDrawerComponent);
  }


  /* =============================================================== */
  /* rte-formatter — ported verbatim from horizon/assets/rte-formatter.js */
  /* Wraps rich-text content. Without registration, browsers treat    */
  /* <rte-formatter> as HTMLUnknownElement (display: inline), breaking */
  /* padding / width / border layout on multi-line content.           */
  /* =============================================================== */
  class RTEFormatter extends Component {
    connectedCallback() {
      super.connectedCallback();
      this.querySelectorAll('table').forEach(this.#formatTable);
    }

    #formatTable(table) {
      const wrapper = document.createElement('div');
      wrapper.classList.add('rte-table-wrapper');
      const parent = table.parentNode;
      if (parent) {
        parent.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      }
    }
  }

  if (!customElements.get('rte-formatter')) {
    customElements.define('rte-formatter', RTEFormatter);
  }

  /* =============================================================== */
  /* deferred-media — ported from horizon/assets/media.js             */
  /* (DeferredMedia class only; the product-model subclass that       */
  /* handles 3D models is omitted — not used in cart context).        */
  /* Lazy-loads video content on user interaction. Without            */
  /* registration, <deferred-media> renders inline and its width /    */
  /* border / radius fail to apply.                                    */
  /* =============================================================== */
  class DeferredMedia extends Component {
    isPlaying = false;

    #abortController = new AbortController();

    connectedCallback() {
      super.connectedCallback();
      const signal = this.#abortController.signal;
      document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
      window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.#abortController.abort();
    }

    updatePlayPauseHint(isPlaying) {
      const toggleMediaButton = this.refs.toggleMediaButton;
      if (toggleMediaButton instanceof HTMLElement) {
        toggleMediaButton.classList.remove('hidden');
        const playIcon = toggleMediaButton.querySelector('.icon-play');
        if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
        const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
        if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
      }
    }

    showDeferredMedia = () => {
      this.loadContent(true);
      this.isPlaying = true;
      this.updatePlayPauseHint(this.isPlaying);
    };

    loadContent(focus = true) {
      if (this.getAttribute('data-media-loaded')) return;

      this.dispatchEvent(new MediaStartedPlayingEvent(this));

      const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

      if (!content) return;

      this.setAttribute('data-media-loaded', 'true');
      this.appendChild(content);

      if (focus && content instanceof HTMLElement) {
        content.focus();
      }

      this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

      if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
        content.play();
      }
    }

    toggleMedia() {
      if (this.isPlaying) {
        this.pauseMedia();
      } else {
        this.playMedia();
      }
    }

    playMedia() {
      const iframe = this.querySelector('iframe[data-video-type]');
      if (iframe) {
        iframe.contentWindow?.postMessage(
          iframe.dataset.videoType === 'youtube'
            ? '{"event":"command","func":"playVideo","args":""}'
            : '{"method":"play"}',
          '*'
        );
      } else {
        this.querySelector('video')?.play();
      }
      this.isPlaying = true;
      this.updatePlayPauseHint(this.isPlaying);
    }

    pauseMedia() {
      const iframe = this.querySelector('iframe[data-video-type]');

      if (iframe) {
        iframe.contentWindow?.postMessage(
          iframe.dataset.videoType === 'youtube'
            ? '{"event":"command","func":"pauseVideo","args":""}'
            : '{"method":"pause"}',
          '*'
        );
      } else {
        this.querySelector('video')?.pause();
      }
      this.isPlaying = false;

      if (this.getAttribute('data-media-loaded')) {
        this.updatePlayPauseHint(this.isPlaying);
      }
    }
  }

  if (!customElements.get('deferred-media')) {
    customElements.define('deferred-media', DeferredMedia);
  }


  // Expose key modules on window.HorizonCart for cart-integration.js
  window.HorizonCart = window.HorizonCart || {};
  if (typeof sectionRenderer !== 'undefined') window.HorizonCart.sectionRenderer = sectionRenderer;
  if (typeof ThemeEvents !== 'undefined') window.HorizonCart.ThemeEvents = ThemeEvents;
  if (typeof CartAddEvent !== 'undefined') window.HorizonCart.CartAddEvent = CartAddEvent;
  if (typeof CartUpdateEvent !== 'undefined') window.HorizonCart.CartUpdateEvent = CartUpdateEvent;
  if (typeof morph !== 'undefined') window.HorizonCart.morph = morph;
  if (typeof morphSection !== 'undefined') window.HorizonCart.morphSection = morphSection;
})();
